import { set as setProp } from "dot-prop";
import { omit } from "lodash";
import { node } from "prop-types";
import ReactDOM from "react-dom";
import {
    channelTypeOptions as allChannelTypeOptions,
    channelTypeToName,
    Constant,
    nodesForExclusion,
    rootModuleId,
    RootRoute
} from "@app/const";
import { defaultServiceContext } from "@app/contexts";
import DesignerSetting from "@app/DesignerSetting";
import { useCanvasTabs, useCurrentModule } from "@app/hooks";
import VariableDataModel from "@app/models/VariableDataModel";
import { setGlobalSettings } from "@app/stores";
import {
    DesignBranchingEventType,
    DesignChannelName,
    DesignChannelType,
    DesignClass,
    DesignItemType,
    DesignRootSettings,
    DesignRuntimeEngine,
    IconClass,
    NodeTypeName,
    ObjectClass,
    Subject,
    ValueOrVariableType,
    VariableType
} from "@app/types";
import { BreakingChangesDialog } from "@app/widgets/dialogs";
import {
    getDuplicateIdItems,
    CheckDesignDialog
} from "@app/widgets/dialogs/check-design";
import CommonModuleChangeVersionDialog from "@app/widgets/dialogs/CommonModuleChangeVersionDialog";
import CommonModuleWizardDialog from "@app/widgets/dialogs/CommonModuleWizardDialog";
import MergeBranchDialog from "@app/widgets/dialogs/MergeBranchDialog";
import { MergeDialog, MergeDialogType } from "@app/widgets/dialogs/MergeDialog";
import Cache from "./Cache";
import CommonModuleManager from "./CommonModuleManager";
import Config from "./Config";
import {
    compareDesignVersions,
    CompareResult,
    createUuid,
    getLatestBranchingEvent,
    navigateToRoute,
    normalizeObjectOrNode,
    renderByRoute,
    setElementClassEnabled,
    updateLastChange,
    updateTree
} from "./functions";
import KnowledgeBase from "./KnowledgeBase";
import Notification from "./Notification";
import { ReactDOMComponents } from "./ReactDOMComponents";
import IntentUsedIn from "./usedIn/IntentUsedIn";
import ModuleUsedIn from "./usedIn/ModuleUsedIn";
import ObjectsUsedIn from "./usedIn/ObjectsUsedIn";
import VariablesUsedIn from "./usedIn/VariablesUsedIn";
import Utility from "./Utility";
import WorkerManager from "./workers/WorkerManager";

/**
 * Make types available in JSDocs without importing them since this is not a TS file.
 *
 * @import {
 *   AvsChangeLogs,
 *   Design,
 *   DesignModuleLink,
 *   DesignModuleNode,
 *   DesignObject
 * } from '@app/types';
 * @import { CustomDiagramWidget } from '@app/widgets/CustomDiagramWidget';
 */

const defaultFillerPropmts = {
    "typing": [
        { "name": "SHORT_TYPING", "transcripts": [{ "locale": "en-US", "transcript": "{typing}." }] },
        { "name": "LONG_TYPING", "transcripts": [{ "locale": "en-US", "transcript": "{typing} {typing} {typing}." }] }
    ],
    "wait": [
        { "name": "FILLER_1", "transcripts": [{ "locale": "en-US", "transcript": "Hold on for just a moment." }] },
        { "name": "FILLER_2", "transcripts": [{ "locale": "en-US", "transcript": "Hold one moment, please." }] },
        { "name": "FILLER_3", "transcripts": [{ "locale": "en-US", "transcript": "Please hold for one moment." }] },
        { "name": "FILLER_4", "transcripts": [{ "locale": "en-US", "transcript": "One moment, please." }] },
        { "name": "FILLER_5", "transcripts": [{ "locale": "en-US", "transcript": "Bear with me just a moment." }] },
        { "name": "FILLER_6", "transcripts": [{ "locale": "en-US", "transcript": "Please hold for one moment." }] },
        { "name": "FILLER_7", "transcripts": [{ "locale": "en-US", "transcript": "Hold on just one moment." }] },
        { "name": "FILLER_8", "transcripts": [{ "locale": "en-US", "transcript": "Please hang on for just a moment." }] },
        { "name": "FILLER_9", "transcripts": [{ "locale": "en-US", "transcript": "Please hold for one moment." }] },
        { "name": "FILLER_10", "transcripts": [{ "locale": "en-US", "transcript": "Please give me a moment." }] },
        { "name": "FILLER_11", "transcripts": [{ "locale": "en-US", "transcript": "Bear with me just one moment, please." }] }
    ],
    "data_access": [
        { "name": "WAIT_AUDIO", "transcripts": [{ "locale": "en-US", "transcript": "{add 2.5sec silence} {typing} {typing} {typing} One moment, please. {typing} {typing} {typing} Please hold on. {typing} Please give me a moment. {typing} {typing} {typing}" }] }
    ]
};

const {
    avsService,
    errorMessageService,
    globalModalsService,
    loadingService,
    lokiService,
    userService
} = defaultServiceContext;

export class AppManager {
    /** @type {Subject<Design>} @readonly Emits when @see serialized changes. */
    serializedChangeSubject = new Subject();
    /** @type {Subject<string>} @readonly Emits when @see latestVersion changes. */
    latestVersionSubject = new Subject();
    /** @type {Subject<Design>} @readonly Emits when @see latestSerialized changes. */
    latestSerializedSubject = new Subject();
    /** @private Whether breaking changes (like enabling branching) have happened between @see serialized & @see latestSerialized version. */
    _breakingChangesExist = false;
    /** @type {string | null} @private Latest saved version number in AVS for this branch. */
    _latestVersion = null;
    /** @type {Design | null} @private Latest saved serialized in AVS for this branch. */
    _latestSerialized = null;
    /** @type {Design} @private */
    _serialized = null;
    /** @type {boolean} @private @see onValidateApplication has been ran since @see serialized was set. */
    _serializedValidated = false;
    /** @type {(() => void) | null} */
    _unsubscribeCanvasTabs = null;

    /** Latest saved serialized in AVS for this branch. */
    get latestSerialized() {
        return this._latestSerialized;
    }
    /** @private */
    set latestSerialized(latestSerialized) {
        if (latestSerialized?.version === this._latestSerialized?.version) {
            return;
        }
        this._latestSerialized = latestSerialized;
        this.latestSerializedSubject.next(latestSerialized);
        this._checkForBreakingChanges();
    }

    /** Latest saved version number in AVS for this branch. */
    get latestVersion() {
        return this._latestVersion;
    }
    /** @private */
    set latestVersion(latestVersion) {
        if (latestVersion === this._latestVersion) {
            return;
        }
        this._latestVersion = latestVersion;
        this.latestVersionSubject.next(latestVersion);
    }

    /** Serialized application. Initialized in @see KnowledgeBase.databaseLoaded. */
    get serialized() {
        return this._serialized;
    }
    set serialized(serialized) {
        const { initTabs, rerenderTabs } = useCanvasTabs.getState();
        const { defaultChannel } = serialized.rootSettings;
        const oldSerialized = this._serialized;

        this._breakingChangesExist = false;
        this._serializedValidated = false;
        this._serialized = serialized;
        this._checkForBreakingChanges();
        /** @type {Parameters<typeof setGlobalSettings>[0]} */
        const globalSettingsUpdates = { serialized };
        if (defaultChannel) {
            globalSettingsUpdates.channel = defaultChannel;
        }
        setGlobalSettings(globalSettingsUpdates);
        this.serializedChangeSubject.next(serialized);
        userService.refreshDesignRoles();
        initTabs(serialized);

        if (
            oldSerialized
            && (
                oldSerialized.branchId !== serialized.branchId
                || oldSerialized.version !== serialized.version
            )
        ) {
            rerenderTabs();
        }

        this.onValidateApplication();
        this.initDisplayPrefs();
        this._updateCurrentModuleStore();
    }

    /**
     * Global context for a single app
     * @module AppManager
     * @param {string} branchId Branch ID for the @see Design to open.
     */
    constructor(branchId) {
        this.branchId = branchId;
        this.notification = new Notification();
        this.cache = new Cache();
        this.config = new Config();
        this.knowledgeBase = new KnowledgeBase();
        this.serializedPendingSave = false;
        this.removedNode = null;
        this.treeNode = null;
        this.pendingVersionRequest = false;
        this.reactDOMComponents = new ReactDOMComponents();
        this.timeOffset = 0;
        this.changeLogMaps = [];
        this.workers = new WorkerManager();
        this.commonModuleManager = new CommonModuleManager();
        this.saveDocumentInterval = null;
        this.checkNotifyInterval = null;
        this._unsubscribeCanvasTabs = useCanvasTabs.subscribe((state, prevState) => {
            if (state.activeTabId !== prevState.activeTabId) {
                this._updateCurrentModuleStore();
            }
        });
    }

    /**
     * Releases everything this instance registered outside of itself.
     *
     * @note Production only ever has one @see AppManager, but tests build a new
     * one per case & would otherwise stack store subscribers.
     */
    dispose() {
        this._unsubscribeCanvasTabs?.();
        this._unsubscribeCanvasTabs = null;
        clearInterval(this.saveDocumentInterval);
        clearInterval(this.checkNotifyInterval);
    }

    /**
     * Gets channel options available on @see serialized based on its @see DesignRootSettings.
     *
     * @returns {{
     *   channelNameOptions: {
     *     name: string;
     *     value: DesignChannelName;
     *   }[];
     *   channelTypeOptions: {
     *     name: string;
     *     value: DesignChannelType;
     *   }[];
     *   channels: DesignRootSettings['channels'];
     *   defaultChannel: DesignChannelType;
     * }}
     */
    getChannelOptions() {
        const {
            channels,
            defaultChannel,
        } = window.apm.serialized.rootSettings;
        const channelTypeOptions = allChannelTypeOptions.filter(({ value }) => {
            return value in channels;
        });
        const channelNameOptions = channelTypeOptions.map((option) => {
            return {
                ...option,
                value: channelTypeToName[option.value]
            };
        });

        return {
            channelNameOptions,
            channelTypeOptions,
            channels,
            defaultChannel
        };
    }

    initTimestamp() {
        const that = this;
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.open("HEAD", "https://health.us-east-1.amazonaws.com/", true);
        xmlhttp.onload = function () {
            if (xmlhttp.readyState === 4) {
                var serverDateStr = xmlhttp.getResponseHeader('Date');
                var serverTimestamp = new Date(serverDateStr).getTime();
                var nowTimeStamp = Date.now();
                that.timeOffset = serverTimestamp - nowTimeStamp;
            }
        };
        xmlhttp.send(null);
    }

    isVoiceChannelEnabled() {
        const { channels } = this.serialized.rootSettings ?? {};
        return !channels || DesignChannelType.voice in channels;
    }

    /** @param {DesignObject} item to display panel for. */
    openPropertyPanel(item) {
        this.postEventToCurrentView({
            event: item,
            shouldFocusInput: true,
            target: "request-component-panel"
        });
    }

    get timestamp() {
        return Date.now() + this.timeOffset;
    }

    updateCIntentsWithId({ compoundIntents }) {
        compoundIntents = compoundIntents.map(cIntent => {
            if (!cIntent.id) {
                cIntent.id = createUuid();
            }
        });
    }

    /**
     * @param {Design} serialized design to add missing last changed properties to CSVs on.
     */
    updateCSVDataWithTimestamp(serialized) {
        let csvData = serialized.csvData || [];
        csvData = csvData.map(csv => {
            if (!csv.lastLocalChange) {
                csv.lastLocalChange = serialized.lastSavedToRepo;
            }
        });
    }

    /**
     * Validate the current serialized application to check for warning that should be brought to the user's attention.
     */
    async onValidateApplication() {
        const { serialized } = this;
        const designValidationElement = document.getElementById('design-validation');
        if (!serialized || !designValidationElement || this._serializedValidated) {
            return;
        }

        this._serializedValidated = true;
        const {
            runtimeEngine = DesignRuntimeEngine.nashorn
        } = serialized.rootSettings ?? {};
        if (!Utility.getIsCommonModule(serialized) && runtimeEngine === DesignRuntimeEngine.nashorn) {
            const { deprecatedNashorn, fixInSettings } = window.l.popup;
            const proceed = await globalModalsService.openConfirmationModal({
                children: deprecatedNashorn,
                labels: {
                    continueButton: fixInSettings,
                    header: 'Runtime Engine Warning'
                },
                id: 'runtime-engine-warning'
            });
            if (proceed) {
                navigateToRoute(RootRoute.settings);
            }
        }

        // Do not run the below validation if the design is not the main branch.
        if (serialized.branchName !== avsService.mainBranchName) {
            return;
        }

        const duplicateIdItems = getDuplicateIdItems(serialized);
        designValidationElement.classList[(duplicateIdItems.length) ? 'remove' : 'add']('hidden');

        this.designValidation = {
            duplicateIdItems
        };
    }

    openDesignValidation() {
        CheckDesignDialog.open();
    }

    /** 
     * @param {Design} serialized design to use as the new object to edit in D2.
     * @param {string | null} [version] that is now the latest version.
     */
    refreshApplication(serialized, version = null) {
        version ??= serialized.version;
        window.apm.serialized = serialized;
        window.apm.checkNotifyVersion();
        window.apm.setBasic("repoVersion", version);
        window.apm.setVersionInfo("latestVersion", version);
        window.apm.setSerializedChanged();

        this.reactDOMComponents.renderApplicationPages();

        // refresh canvas and trees and settings
        window.apm.postEventToAllViews({target: "request-reload-data-model"});
        // TODO: should close all property panels
        // update property panels
        document.dispatchEvent(new CustomEvent("ev-property-refresh"));
    }

    /**
     * Intialize treeviews and setup auto-save
     */
    async init() {
        console.time("Begin init");

        this.initTimestamp();
        // IMPORTANT: as a result of knowledgeBase.loadTree, knowledgebase assigns serialized to window.apm (a.k.a this.serialized) in knowledgebase.databaseLoaded
        const serialized = await this.knowledgeBase.loadTree();
        // clean up routines
        //DEVDES2-1702: Common modules export multiple of same prompt
        if(Utility.getIsCommonModule(serialized)){
            this.cleanUpCommonModuleDuplicatePromptsInSerialized(serialized);
        }
        //DEVDES2-1716: multiple objects in nested common modules
        if(Utility.getIsCommonModule(serialized)){
            this.cleanUpComponentsInCommonModule(serialized);
        }

        // temporary?
        if (!serialized.compoundIntents) {
            serialized.compoundIntents = [];
            this.setSerializedChanged();
        }

        this.updateCIntentsWithId(serialized);
        
        this.updateCSVDataWithTimestamp(serialized);

        let intervalsStarted = false;
        const startIntervalsAfterRestore = () => {
            if (
                intervalsStarted
                || window.location.host === 'designer2.interactions.com'
            ) {
                return;
            }

            intervalsStarted = true;
            this.initDocumentSetIntervals();
        };

        // get latest version before initializing design.json, etc.
        // DEV: excluding Progressive D1CustomerService as inconsistant search results are sometimes empty
        if (DesignerSetting.s.is_public === 'true') {
            this.latestSerialized = window.apm.serialized;
            this.latestVersion = window.apm.serialized.version;
            this.setChangeLogMaps([]);
            return;
        }

        const { branchId } = window.apm.serialized;

        try {
            const latestVersion = (
                await avsService.getLatestVersion(branchId)
            )?.version;
            // Intervals only after restore reselect - safe vs Stage A root dirty flush.
            startIntervalsAfterRestore();

            if (!latestVersion) {
                this.setVersionInfo('notYetSaved');
                this.latestSerialized = window.apm.serialized;
                this.latestVersion = window.apm.serialized.version;
                this.setChangeLogMaps([]);
                return;
            }

            this.latestVersion = latestVersion;
            this.setVersionInfo('latestVersion', latestVersion);

            const application = await avsService.getDesign(branchId, latestVersion);
            this.latestSerialized = application;
            this.latestVersion = application.version;

            const cmaps = await avsService.getChangeLogs(branchId, latestVersion);
            this.setChangeLogMaps(cmaps);
            console.timeEnd("Begin init");
        } catch {
            window.apm.setVersionInfo('offline');
            this.latestSerialized = null;
            this.latestVersion = null;
            this.setChangeLogMaps([]);
            startIntervalsAfterRestore();
        }
    }

    initDocumentSetIntervals() {
        this.saveDocumentInterval = setInterval(async () => {
            if (this.serializedPendingSave) {
                this.serializedPendingSave = false;
                this.serialized.lastUpdate = window.apm.timestamp;
                await lokiService.storeSerialized(this.serialized, { confirmConflict: false });
                $('#saving-status').html(window.l.header.all_changes_saved);
                this.printDebug("Database updated", this.serialized.lastUpdate, Constant.k.DEBUG_STORAGE);
                // Shallow copy to force components to re-render.
                this.serializedChangeSubject.next({ ...this.serialized });
                this._updateCurrentModuleStore();
            }
        }, 2000);

        this.checkNotifyInterval = setInterval(() => {
            // get latest version
            this.checkNotifyVersion();
        }, 7000); // TODO: coordinate loading xhr requests with this interval

    }

    /**
     * Replaces local version of application with latest version on AVS
     * Reloads window with latest application version
     */
    async openLatestVersion() {
        const { latestVersion, latestSerialized } = window.apm;
        if (!latestVersion) {
            return;
        }

        const { branchId } = window.apm.serialized;
        let serialized = (latestSerialized?.version === latestVersion) ? (
            latestSerialized
        ) : null;
        if (!serialized) {
            await loadingService.useLoading(async () => {
                serialized = await avsService.getDesign(branchId, latestVersion)
            }, `Downloading version ${latestVersion}...`);
        }

        serialized = await loadingService.useLoading(async () => {
            return await lokiService.storeSerialized(serialized);
        }, 'Storing new version');
        if (serialized == null) {
            // User chose to keep their local changes.
            return;
        } else if (serialized.version === latestVersion) {
            const url = new URL(window.location.href);
            const query_string = url.search;
            const search_params = new URLSearchParams(query_string);

            search_params.set('g', serialized.branchId);
            url.search = search_params.toString();
            window.location.replace(url.toString().split('#')[0]);
        } else {
            alert('Failed to download latest version. Please try again.');
        }
    }

    /**
     * cleaning up the commonModule components that are not removed when common module is deleted in authoring mode
     * to see what the issue is open FullNameCapture version 1.0.11 and try changing the version 
     * instead of updating the version nwe are importing the new insatnce of commonmodule with the seleted version because of inconsistancy between moduleId's 
     * when you we delete the newly imported one the objets are not deleted, so when this common module is imported into app 
     * we are seeing the multiple insatnces of prompt insated of seeing only one
     */
    cleanUpComponentsInCommonModule(serializedCommomModule) {
        serializedCommomModule.components.objects.forEach((object, objectIdx) => {
            if (object.isHidden) {
                const modIdx = serializedCommomModule.components.modules.findIndex(mod => mod.id === object.props.moduleId)
                
                // has bad link to parent common module
                if (modIdx === -1) {
                    const objUsedIn = ObjectsUsedIn.getObjectUsedIn(object.id, serializedCommomModule);
                    const usedInFlows = objUsedIn[0].length > 0;
                    
                    // fix in flows 
                    if (usedInFlows > 0) {
                        objUsedIn[0].forEach(usedInModId => {
                            const usedInModIdx = serializedCommomModule.components.modules.findIndex(module => module.id === usedInModId);
                            const usedInModule = serializedCommomModule.components.modules[usedInModIdx];
                             // if it is rootModule and it is common module
                            if (usedInModule.props.isRootModule &&usedInModule.props.isCommonModule&& usedInModule.props.moduleId !== usedInModule.id) {
                                usedInModule.props.moduleId = usedInModule.id;
                                usedInModule.props.moduleApp = usedInModule.name;
                            }
                            // this fixes the inconsitency moduleId and moduleApp
                            object.props.moduleId = usedInModule.id;
                            object.props.moduleApp = usedInModule.name;
                            object.props.version = usedInModule.props.commonModuleVersion;
                            //update moduleApp and moduleId of flow instances
                            const objInstanceIndex= serializedCommomModule.components.modules[usedInModIdx].versions[1].flow.nodes.findIndex(n=>n.props.parentComponentID===object.id);
                            serializedCommomModule.components.modules[usedInModIdx].versions[1].flow.nodes[objInstanceIndex].props.moduleId = usedInModule.id;
                            serializedCommomModule.components.modules[usedInModIdx].versions[1].flow.nodes[objInstanceIndex].props.moduleApp = usedInModule.name;
                            serializedCommomModule.components.modules[usedInModIdx].versions[1].flow.nodes[objInstanceIndex].props.version = usedInModule.props.commonModuleVersion;
                        })
                    }
                    // not used in flows, just deleted from application
                    else {
                        serializedCommomModule.components.objects.splice(objectIdx, 1)
                    }
                }
            }

        })
        return serializedCommomModule
    }

    /**
     * Remove unused duplicate common module prompts from application.
     *
     * @param {Design} serialized design to dedupe prompts in.
     * @returns {Design} cleaned up serialized.
     */
    cleanUpCommonModuleDuplicatePromptsInSerialized(serialized) {
        // find all como prompts (isHidden)
        serialized.components.objects.filter(object => object.props.nodeTypeName === NodeTypeName.prompt && object.isHidden).forEach(prompt => {
            // all prompts matched by name
            const PROMPTS_BY_NAME = serialized.components.objects.filter(object => object.props.nodeTypeName === NodeTypeName.prompt && object.name === prompt.name);
            
            // clean up duplicate prompts
            if(PROMPTS_BY_NAME.length > 1){
                PROMPTS_BY_NAME.forEach(matchedPrompt => {
                    const usedIn = ObjectsUsedIn.getObjectUsedIn(matchedPrompt.id, serialized);
                    
                    // if it is not used, delete from app and add to deletedMap
                    if(usedIn[0].length === 0){
                        this.updateDeletedApplicationObjectsMap('objects', matchedPrompt.id)
                        serialized.components.objects = serialized.components.objects.filter(obj => obj.id !== matchedPrompt.id);
                    }
                    
                });
            }
        
        });

        return serialized;
    }

    /**
     * Fetch latestVersion info and download in this is not latest
     */
    async checkNotifyVersion() {
        const { branchId } = window.apm.serialized;

        try {
            const latestVersion = (
                await avsService.getLatestVersion(branchId)
            )?.version;
            if (latestVersion == null) {
                window.apm.setVersionInfo('notYetSaved');
                return;
            }

            const shouldDownload = latestVersion !== window.apm.latestSerialized?.version;
            this.setVersionInfo('latestVersion', latestVersion);
            if (!shouldDownload) {
                return;
            }

            const latestApplication = await avsService.getDesign(branchId, latestVersion);
            this.latestSerialized = latestApplication;
            this.latestVersion = latestApplication.version;
            this.setVersionInfo('latestVersion', latestApplication.version);

            // refresh changelogs component in save page
            const cmaps = await avsService.getChangeLogs(branchId, latestVersion);
            this.setChangeLogMaps(cmaps);
        } catch {
            window.apm.setVersionInfo('offline');
        }
    }

    setAppProperty(property) {
        const ts = window.apm.timestamp;
        let existed;
        switch (property.type) {
            case "variableBinding":
                property.default = property.default ? property.default : null;
                existed = window.apm.serialized.appProperties.find(p => p.config === property.config);
                break;
            case "callEncryption":
                existed = window.apm.serialized.appProperties.find(p => p.type === "callEncryption");
                break;
        }
        const propertyKeys = Object.keys(property);
        if (existed) {
            propertyKeys.forEach(key => existed[key] = property[key]);
            existed.lastLocalChange = ts;
        } else {
            property.createdTime = ts;
            property.lastLocalChange = ts;
            window.apm.serialized.appProperties.push(property);
        }
        window.apm.setSerializedChanged();
        renderByRoute(RootRoute.settings);

    }

    setVersionInfo(info, version) {
        const $vi = $('#version-info');
        $vi.removeClass('warning');
        $vi.removeClass('error');

        if (info === 'latestVersion') {
            $vi.html(version + " is the latest version");
        } else if (info === 'notYetSaved') {
            $vi.addClass('warning');
            $vi.html("Application has not yet been saved to repo");
        } else if (info === 'offline') {
            $vi.addClass('error');
            $vi.html("Please check your network connection");
        }
    }

    /**
     * Get the author of the current design.
     * @returns {string}
     */
    getDesignerAuthor(){
        return DesignerSetting.s.author;
    }

    /**
     * NOTE: Keeping this while changelog feature is being developed - RD
     *
     * @memberof AppManager
     */
    getChangeLogDialog() {
        window.apm.popup("Change Logs",
            '<div class="new_element_container"></div>',
            `<button class="w2ui-btn primary close-button">${window.l.popup.close}</button> `,
            [
                ".new_element_container",

                () => {
                    const $el = $(".new_element_container");
                    this.changeLogDialog = this.reactDOMComponents.renderDialog({ name: 'changeLogDialog', props: { serialized: window.apm.serialized }, $el });
                    $(".w2ui-popup").find(".close-button").on("click", () => {
                        this.reactDOMComponents.unmountComponentAtNode($el);
                        w2popup.close();
                    });
                
                }
            ],
            700, // height
            null, // node
            null, // treenode
            false // showClose
        );
    }

    getDefaultFillerPrompts() {
        return defaultFillerPropmts;
    }

    /**
     * Change auto-save info display
     */
    setSerializedChanged() {
        this.serializedPendingSave = true;
        $('#saving-status').html(window.l.header.saving);
    }

    /**
     * Return serialized based on node parent context (e.g., application or common module)
     * @param {node} node 
     */
    getSerializedFromNode(node){
        console.warn('this method uses appCommonModules');
        const isCommonModuleApplication = window.apm.serialized.isCommonModule;

        if(!isCommonModuleApplication && node.props.moduleId && !node.props.isRootModule){
            const commonModuleSerialized = window.apm.serialized.appCommonModules || [];
            return commonModuleSerialized.find(cm => cm._id === node.props.moduleId && cm.version === node.props.version);
        } 
        return window.apm.serialized;
    }

    handleNoWritePermission() {
        $('#saving-status').html(window.l.header.changes_cannot_be_saved);
        window.apm.notification.post("error", window.l.notification.no_permission);
    }

    /**
     * Set basic application settings
     * @param {string} key
     * @param {*} value
     */
    setBasic(key, value) {
        if (key === "client" || key === "application") {
            this.serialized._id = createUuid();

            if (key === "client") {
                window.apm.serialized.repoClient = value;
            } else {
                window.apm.serialized.repoApplication = value;
            }

            // update save props
            this.reactDOMComponents.render(RootRoute.save);
        }
        if (key !== "lastUpdate" && key !== "components" && key !== "_id") {
            if ((key === "application" || key === "client") && this.serialized[key] !== value) {
                this.serialized["_id"] = createUuid();
            }
            if (key === "repoVersion") {
                this.serialized['version'] = value;
            }
            setProp(this.serialized, key, value);
        }

        if (key === "description") {
            this.serialized.descriptionLastLocalChange = window.apm.timestamp;
        } else if (key.startsWith('rootSettings')) {
            this.serialized.rootSettings.lastLocalChange = window.apm.timestamp;
        }

        // rerender test tool widget when application graphQLSchemas have changed 
        if (key === "graphQLSchemas") {
            this.reactDOMComponents.render(RootRoute.testTool);
        }

        this.setSerializedChanged();
        document.title = this.serialized.clientName + " " + this.serialized.designName + " " + this.serialized.version;
    }

    /**
     * Get variable suggestions within application scope
     * @param {string} query
     * @returns {VariableDataModel[]}
     */
    searchVariable(query = "", reference = null) {
        const scopeFilter = (variable) => {
            switch (reference) {
                case "conversationVariable":
                    return variable.props.config.scope === "conversation";
                default:
                    return (
                        !variable.props.config.scope
                        || variable.props.config.scope === "session"
                    );
            }
        };
        const typeFilter = (variable) => {
            switch (reference) {
                case "moduleReference":
                    return variable.props.config.type === "MODULE";
                case "promptReference":
                    return variable.props.config.type === "PROMPT";
                default:
                    return true;
            }
        };

        return this.serialized.components.variables.filter((variable) => {
            return (
                scopeFilter(variable)
                && typeFilter(variable)
                && (
                    !query
                    || VariableDataModel.match(query, variable.props.config)
                )
            );
        }).map((matchedVariable) => {
            return new VariableDataModel(matchedVariable.props);
        });
    }

    hasPermission(nodeId, permission = "read") {
        const node = this.getNode(nodeId);
        if (!node) {
            return true;
        }
        var definedPermission = {};
        if (node.props && node.props.permission) {
            definedPermission = node.props.permission;
        }
        const currentUserGroup = window.colm.user.userGroup;
        if (definedPermission.hasOwnProperty(currentUserGroup)) {
            return definedPermission[currentUserGroup][permission];
        } else {
            const groups = Constant.k.USER_GROUP;
            return groups[currentUserGroup].defaultValue[permission];
        }
    }

    exportNode(nodeId) {
        const exports = {
            node: JSON.parse(JSON.stringify(this.getNode(nodeId))),
            _dependencies: [],
            _commonModules: []
        };
        if (exports.node.type === "module") {
            this.getModuleDependencies(exports.node.id, exports._dependencies, exports._commonModules);
        }
        return exports;
    }

    getModuleDependencies(moduleId, dependencies, commonModules) {
        const module = this.getNode(moduleId);
        const configKeys = Object.keys(module.props.config);
        const addDependency = (node, type = null) => {
            if (!node) {
                return false;
            }
            const dependency = dependencies.find(element => element.id === node.id);
            if (dependency == null) {
                dependencies.push({
                    id: node.id,
                    type: type ?? node.type,
                    usedIn: [module.id],
                    node: JSON.parse(JSON.stringify(node))
                });
                return true;
            } else if (!dependency.usedIn.includes(module.id)) {
                dependency.usedIn.push(module.id);
            }
            return false;
        };
        configKeys.forEach(key => {
            if (key === "inputs" || key === "outputs") {
                module.props.config[key].forEach(keyItem => {
                    const io = keyItem;
                    if (io.varName && io.varName.indexOf(".") !== -1) {
                        const scope = io.varName.split(".")[0];
                        const varName = io.varName.split(".")[1];
                        if (scope === Constant.k.SCOPE_APPLICATION) {
                            const variable = window.apm.getVariable(varName);
                            if (variable) {
                                addDependency(variable);
                            }
                        }
                    }
                });
            }
        });
        const flow = module.versions[1].flow;
        const flowKeys = Object.keys(flow);
        flowKeys.forEach(fKey => {
            if(fKey === "links" || fKey === "nodes") {
                flow[fKey].forEach(item => {
                    const flowItem = item;
                    if (fKey === "nodes" && flowItem.props?.parentComponentID && flowItem.type === "object") {
                        if (flowItem._class === ObjectClass.savePrompt && flowItem.props.config.playSavedPromptId) {
                            const parentObject = this.getNode(flowItem.props.config.playSavedPromptId);
                            addDependency(parentObject);
                        }
                        if (
                            [ObjectClass.savePrompt, ObjectClass.playSavedPrompt].includes(flowItem._class)
                            && flowItem.props.config.promptId
                        ) {
                            const parentObject = this.getNode(flowItem.props.config.promptId);
                            addDependency(parentObject);
                        }
                        const parentObject = this.getNode(flowItem.props.parentComponentID);
                        if (parentObject == null && !nodesForExclusion.has(flowItem._class)) {
                            const error = new Error(`Node '${flowItem.name}' in module '${module.name}' is missing an application level object.`);
                            errorMessageService.addError(error)
                            throw error;
                        }
                        addDependency(parentObject);
                    }
                    if (flowItem.props && flowItem.props.config) {
                        const lnConfigKeys = Object.keys(flowItem.props.config);
                        lnConfigKeys.forEach(cKey => {
                                // find variable references
                            if (cKey === "inputs" || cKey === "outputs") {
                                flowItem.props.config[cKey].forEach(item => {
                                    const io = item;
                                    if (io.varName && io.varName.indexOf(".") !== -1) {
                                        const scope = io.varName.split(".")[0];
                                        const varName = io.varName.split(".")[1];
                                        if (scope === Constant.k.SCOPE_APPLICATION) {
                                            const variable = window.apm.getVariable(varName);
                                            if (variable) {
                                                addDependency(variable);
                                            }
                                        }
                                    }
                                });
                            }

                            // find function references
                            if (cKey === "function") {
                                if(flowItem.props.config[cKey].ref){
                                    const func = this.knowledgeBase.getFunctions(flowItem.props.config[cKey].ref);
                                    if (func) {
                                        addDependency(func, "function");
                                        if(func.parameters){
                                            func.parameters.forEach(param => {
                                                if(param.value !== null){
                                                    const varName = param.value.match(/app./) ? param.value.split(".")[1] : false;
                                                    if(varName){
                                                        const variable = window.apm.getVariable(varName);
                                                        if(variable){
                                                            addDependency(variable);
                                                        }
                                                    }
                                                }
                                            });
                                            if(func.script){
                                                const appVariables = window.apm.serialized.components.variables;
                                                appVariables.forEach(variable => {
                                                    const varName = variable.props.config.varName;
                                                    const varNamematch = func.script.match(varName);

                                                    if(varNamematch) {
                                                        addDependency(variable);
                                                    }
                                                });
                                            }
                                        }
                                    }
                                }
                                if(flowItem.props.config[cKey].script){
                                    
                                    const appVariables = window.apm.serialized.components.variables;
                                    appVariables.forEach(variable => {
                                        const varName = variable.props.config.varName;
                                        const varNamematch = flowItem.props.config[cKey].script.match(varName);

                                        if(varNamematch) {
                                            addDependency(variable);
                                        }
                                    });
                                    
                                }
                            }

                            // find schema references
                            if (cKey === "schema") {
                                const schema = this.knowledgeBase.getSchemas(flowItem.props.config[cKey]);
                                if (schema) {
                                    addDependency(schema, "schema");
                                }
                            }

                            // find csv references
                            if (cKey === "csvData") {
                                const csv = this.knowledgeBase.getCSVData(flowItem.props.config[cKey]);
                                if (csv) {
                                    addDependency(csv, "csvData");
                                }
                            }

                            // find intent references
                            if (cKey === "localIntents" || cKey === "expectedIntent" || cKey === "impliedIntent") {
                                var intentArray;

                                if (cKey === "localIntents") {
                                    intentArray = flowItem.props.config[cKey];
                                } else {
                                    intentArray = [flowItem.props.config[cKey]];
                                }

                                intentArray.forEach(intentItem => {
                                    const intent = this.getNode(intentItem);

                                    if (intent) {
                                        addDependency(intent);
                                    }
                                });
                            }
                            
                        });
                    }
                    // find module references
                    if (flowItem.props && flowItem.props.ref) {
                        const mod = this.getNode(flowItem.props.ref.split("::")[0]);
                        if (mod) {
                            const wasNewlyAdded = addDependency(mod);
                            if (wasNewlyAdded) {
                                if (mod.props.isCommonModule && mod.props.isRootModule) {
                                    const commonModule = window.apm.serialized.appCommonModules.find(element => {
                                        return element.application === mod.repoName;
                                    });
                                    if (commonModule) {
                                        commonModules.push(commonModule);
                                    } else {
                                        console.error(`Common module '${mod.repoName}', used by module '${mod.name}', is not in appCommonModules.`);
                                    }
                                }
                                this.getModuleDependencies(mod.id, dependencies, commonModules);
                            }
                        }
                    }
                });
            }
        });
    }
   
    updateGlobalIntent(node,valKey,newValue) {
        const globalModuleIndex = window.apm.serialized.globalIntents.findIndex(gi => gi.module && gi.moduleProperties.id === node.id);
        if (valKey==="inputs"&&globalModuleIndex !== -1) {
            if (newValue.key === "value") {
                window.apm.serialized.globalIntents[globalModuleIndex].moduleProperties.props.config.inputs[newValue.inputIndex].value =  newValue.value;
            }
            else if (newValue.key === "variable") {
                window.apm.serialized.globalIntents[globalModuleIndex].moduleProperties.props.config.inputs[newValue.inputIndex].varName = newValue.value;
            }
            
            window.apm.serialized.globalIntents[globalModuleIndex].moduleProperties.props.config.inputs[newValue.inputIndex].type = newValue.key;
            updateLastChange(window.apm.serialized.globalIntents[globalModuleIndex]);
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        
        }
        if (valKey === "name"&&globalModuleIndex !== -1) {
            window.apm.serialized.globalIntents[globalModuleIndex].moduleProperties.name = newValue;
            updateLastChange(window.apm.serialized.globalIntents[globalModuleIndex]);
        }
        window.apm.setSerializedChanged();
        renderByRoute(RootRoute.intents);
    }

    /** @returns {AvsChangeLogs}. */
    getChangeLogEntry({ author, latestVersion, version, changeLogMap = null, initial = false, commitMessage = "" }) {
        const newVersion = (initial) ? latestVersion : version;
        const clm = (changeLogMap) ? changeLogMap : [[], []];
        const timeStamp = window.apm.timestamp;
        const NON_COLLAB = Utility.getIsNonCollaboration();

        return {
            version: newVersion,
            author: author,
            timeStamp: timeStamp,
            changeLogMap: clm,
            isCollaborationBranch: !NON_COLLAB,
            commitMessage,
            d2commit: DesignerSetting.s.commit,
            env: DesignerSetting.s.env,
            savedFrom: window.apm.serialized.version
        };
    }

    /**
     * Sets local copy of change log maps and re-renders the ChangeLog component
     * @param {array} cmaps 
     */
    setChangeLogMaps(cmaps) {
        this.changeLogMaps = cmaps;
        this.reactDOMComponents.render('changeLog');
    }

    /**
     * Re-fetch change logs and re-render the ChangeLog region on the Save page when present.
     */
    async refreshChangeLogPanel() {
        if ($("#changelogs-container").length === 0) {
            return;
        }
        const { serialized } = this;
        if (!serialized) {
            return;
        }
        const { branchId } = serialized;
        const version = this.latestVersion ?? serialized.version;
        if (!version || !branchId) {
            return;
        }
        try {
            const cmaps = await avsService.getChangeLogs(branchId, version);
            this.setChangeLogMaps(cmaps);
        } catch {
            // Offline or request failure: keep current panel
        }
    }
            
    getAttachments(type) {
        switch (type) {
            case "function":
                return window.apm.serialized.functions ? window.apm.serialized.functions : [];
            case "schema":
                return window.apm.serialized.graphQLSchemas ? window.apm.serialized.graphQLSchemas : [];
            case "csvData":
                return window.apm.serialized.csvData ? window.apm.serialized.csvData : [];
            case "globalIntent":
                return window.apm.serialized.globalIntents ? window.apm.serialized.globalIntents : [];
            case "compoundIntent":
                return window.apm.serialized.compoundIntents ? window.apm.serialized.compoundIntents : [];
            default:
                return window.apm.serialized.components[type + "s"];
        }
    }

    /**
     * Get an item from a serialized design.
     *
     * @deprecated Use @see Utility.getNode directly instead.
     * @param {Parameters<typeof Utility.getNode>} args to pass through to @see Utility.getNode.
     * @returns {ReturnType<typeof Utility.getNode>} or {null} if unfound.
     */
    getNode(...args) {
        return Utility.getNode(...args);
    }

    renamePrompt(oldName, newName, prompt) {
        const promptUsedIn = ObjectsUsedIn.getObjectUsedIn(prompt.id, this.serialized, prompt.props.config);

        // usedInGlobalIntents
        promptUsedIn[2].forEach(globalIntent=>{
            const gIntent = this.serialized.globalIntents.find(g=>g.id === globalIntent.gi.id)
            if(gIntent.moduleProperties){
                let localVariables =  this.serialized.components.modules.find(mod=>mod.id===gIntent.module).localVariables;
                gIntent.moduleProperties.props.config.inputs.forEach(input => {
                    localVariables.forEach(localVariable => {
                        if (input.argumentName === "mod." + localVariable.name && localVariable.props.config.type === "PROMPT" && input.type === "value" && input.value === oldName) {
                            input.value = newName;
                            updateLastChange(gIntent);
                        }
                    });
                });
            }
        });
        
        // usedInLocalVariables
        promptUsedIn[3].forEach(m=>{
            if(m.variable && m.variable.props.config.type==="PROMPT"&& m.variable.props.config.defaultValue === oldName){
                m.variable.props.config.defaultValue = newName;
                updateLastChange(m.variable.props);
            }
        });

        // usedInModuleInputs
        promptUsedIn[4].forEach(m=>{
            let module =  this.serialized.components.modules.find(mod=>mod.id===m.mod.id);
            let parentModule = this.serialized.components.modules.find(mod=>mod.id===m.nod.props.parentComponentID);
            let nod = module.versions[1].flow.nodes.find(n=>n.id === m.nod.id);
                if(nod.props.config.inputs){
                nod.props.config.inputs.forEach(inp=>{
                    parentModule.localVariables.forEach(localVariable => {
                        if (inp.argumentName === "mod." + localVariable.name && localVariable.props.config.type === "PROMPT" && inp.type === "value" && inp.value === oldName) {
                            inp.value = newName;
                            nod.props.lastLocalChange = this.timestamp;
                        }
                    });
                })
                parentModule.props.config.inputs.forEach(input=>{
                    parentModule.localVariables.forEach(localVariable => {
                        if (input.argumentName === "mod." + localVariable.name && localVariable.props.config.type === "PROMPT" && input.type === "value" && input.value === oldName) {
                            input.value = newName;
                            updateLastChange(parentModule.props);
                        }
                    });
                })
            }
        });

        renderByRoute(RootRoute.intents);
        this.setSerializedChanged();
    }
    /**
     * reset the scope of conversation variables to session 
     * Variables scope can be conversation only if omni channel is true
    */
    resetAppVarScope(){
        this.serialized.components.variables.forEach(variable=>{
            if(variable.props.config.scope === "conversation"){
                variable.props.config.scope = "session";
                variable.props.lastLocalChange = this.timestamp;
            }
        })
        
        this.refreshApplicationTree();
    }

    // CM TODO: handle alias for CM
    renameModule(oldName, newName) {
        this.serialized.components.modules.forEach(mod => {
            // update mod name in inputs that are localVars
            // TODO: 850 changed where mod inputs are found. Should this be removed?
            if (mod.props.config.inputs) {
                mod.props.config.inputs.forEach(input => {
                    mod.localVariables.forEach(localVariable => {
                        const inputIsLocalVarariable = input.argumentName === "mod." + localVariable.name && localVariable.props.config.type === "MODULE" && input.type === "value" && input.value === oldName;
                        if (inputIsLocalVarariable) {
                            input.value = newName;
                            updateLastChange(mod.props);
                        }
                    });
                });
            }
            // update mod name is globalIntents moduleProperties
            if(this.serialized.globalIntents){
                this.serialized.globalIntents.forEach(globalIntent => {
                    if (globalIntent.module === mod.id && globalIntent.moduleProperties.props.config.inputs) {
                        globalIntent.moduleProperties.props.config.inputs.forEach(input => {
                            mod.localVariables.forEach(localVariable => {
                                const inputIsLocalVarariable = input.argumentName === "mod." + localVariable.name && localVariable.props.config.type === "MODULE" && input.type === "value" && input.value === oldName;
                                if (inputIsLocalVarariable) {
                                   input.value = newName;
                                   updateLastChange(globalIntent.moduleProperties.props);
                                }
                            });
                        });
                    }
                });
            }
            // update mod name in inputs that are localVars for all mod instances (flow nodes)
            mod.versions[1].flow.nodes.forEach(node => {
                if (node._class === DesignClass.module && node.props.config.inputs) {
                    node.props.config.inputs.forEach(input => {
                        const parentMod = this.getNode(node.props.parentComponentID);
                        parentMod.localVariables.forEach(localVariable => {
                            const inputIsLocalVarariable = input.argumentName === "mod." + localVariable.name && localVariable.props.config.type === "MODULE" && input.type === "value" && input.value === oldName;
                            if (inputIsLocalVarariable) {
                                input.value = newName;
                                updateLastChange(node.props);
                                this.postEventToAllViews({ target: "request-refresh", event: { id: node.id, type: "node", upDatedField: "inputs", props: node.props } });
                            }
                        });
                    });
                }
            });
            // update mod localVar defaultValue
            if (mod.localVariables && mod.localVariables.length > 0) {
                mod.localVariables.forEach(localVar => {
                    if (localVar.props.config && localVar.props.config.type === "MODULE" && localVar.props.config.defaultValue === oldName) {
                        localVar.props.config.defaultValue = newName;
                        updateLastChange(localVar.props);
                    }
                })
            }
        });

        this.setSerializedChanged();

        //this.reactDOMComponents.render({ name: 'IntentsWidget', props: { serialized: window.apm.serialized }, $el: $('#intents') });

    }

    /**
     * Allow Custom Nodes to share same number of inputs but individual instances maintain type, values, and variable references
     * by assigning qualified changes over original array, adding new inputs to returned array as well
     * @param {object} originalNode any custom node with props not directly being changed
     * @param {object} contextConfig the custom node with props currently being directly changed
     * @param {boolean} isContextNode is the original node the same as the context node 
     * @returns merged inputs as array
     */

    getCustomNodeInputs(originalNode, contextConfig, isContextNode = false) {
        const originalConfig = _.cloneDeep(originalNode);
        let originalArray = (originalConfig.function?.arguments || originalConfig.matchOrder) || [];
        const currentConfig = _.cloneDeep(contextConfig);
        const currentArray = (currentConfig.function?.arguments || currentConfig.matchOrder) || [];
        const removedItem = (window.localStorage.getItem('removedItem')) ? JSON.parse(window.localStorage.getItem('removedItem')) : null;

        // find match
        // length check added because updating properties shouldn't add or subtract arguments - DEVDES2-1331
        if(currentArray.length === originalArray.length) {
            originalArray.forEach((originalArgument,idx) => {
                // Current argument determined by index to accommodate name changes - DEVDES2-1331
                const currentArgument = currentArray[idx];
                
                if(currentArgument){
                    if(!isContextNode){
                        // When the arguments of the same index don't have matching names, this signifies a name change - DEVDES2-1331
                        if(originalArgument.argumentName !== currentArgument.argumentName) {
                            originalArgument.argumentName = currentArgument.argumentName;
                        } 
                    } else {
                        originalArgument = Object.assign(originalArgument, currentArgument);
                    }
                }
            });
        } else if (originalArray.length !== currentArray.legnth) {
        
            // deleted item
            if(removedItem){
                originalArray = originalArray.filter(arg => arg.argumentName !== removedItem.argumentName);
            }

            // added items
            currentArray.forEach(ca => {
                if(originalArray.findIndex(oa => oa.argumentName === ca.argumentName) === -1){
                    originalArray.push(ca);
                }
            });
         }

        return originalArray;
    }

    /**
     * Reset the props and config fields on component objects for disabedInFlow
     * @param {object} object the object to be modified
     * @param {object} context the updated object
     * @param {boolean} isParentObj true if Application Object (vs flow object)
     */
    resetObjectPropsAndConfig(object, context, isParentObj) {
        // maintain objects disabledInFlow value
        const elementDisabled = object.props.config.disabledInFlow;

        // copy updated context config to object
        object.props.config = _.cloneDeep(context.props.config);
        
        // restore disabledInFlow value for flow objects
        if (!isParentObj) {
            object.props.config.disabledInFlow = elementDisabled;
        } else { // app component objects' disabledInFlow is always false
            object.props.config.disabledInFlow = false;
        }

        return object.props.config;
    }

    // TODO:
    // why do we rename modules when dragging cm onto canvas from app panel?
    // handle alias for CM
    // 
    /**
     * @param {unknown} oldValue to replace with @see newValue.
     */
    updateParentNode(context, currentID, valKey, newValue, _type, oldValue = null) {
        const { isTabOpen, renameTab } = useCanvasTabs.getState();
        const { modules } = this.serialized.components;

        // update application components
        switch (context.type) {
            case "module":
                var currentModIndex = modules.findIndex((mod) => mod.id === currentID);
                if (currentModIndex !== -1) {
                    if (valKey === 'name') {
                        updateLastChange(this.serialized.components.modules[currentModIndex].props);
                        this.serialized.components.modules[currentModIndex][valKey] = newValue;
                        this.serialized.globalIntents.forEach(i => {
                            if(i.module === currentID) {
                                i.moduleProperties.name = newValue;
                            }
                        });
                        renameTab(currentID);
                        
                        if(oldValue!==""){
                            //don't run this function if the module oldname is empty because if any module input value is empty we are updating all the input values to module new name which shouldn't happen
                            this.renameModule(oldValue, newValue);
                        }
                        const treeL = $('#tl_container');
                        treeL.jstree().rename_node(currentID, newValue);
                        renderByRoute(RootRoute.intents);
                        renderByRoute(RootRoute.settings);
                     }
                    if (valKey === 'description') {
                        this.serialized.components.modules[currentModIndex].props.config.description = newValue;
                        updateLastChange(this.serialized.components.modules[currentModIndex].props);
                    }
                    if(valKey === 'version'){
                        this.serialized.components.modules[currentModIndex].props.version = newValue;
                        this.serialized.components.modules[currentModIndex].props.config.version = newValue;
                        updateLastChange(this.serialized.components.modules[currentModIndex].props);
                    }
                    if (valKey === 'colorClass' || valKey === "iconClass") {
                        this.serialized.components.modules[currentModIndex].props[valKey] = newValue;
                    }
                }
                break;
            case "object":
                var objects = this.serialized.components.objects;
                var currentObjIndex = objects.findIndex((obj) => obj.id === currentID);
                const contextConfig = _.cloneDeep(context.props.config);
                if (currentObjIndex !== -1) {
                    const parentConfig = _.cloneDeep(this.serialized.components.objects[currentObjIndex].props.config);
                
                    if (valKey === 'name') {
                        if(oldValue!==""){
                            //don't run this function if the prompt oldname is empty because if any module input value is empty we are updating all the input values to prompt new name which shouldn't happen
                           this.renamePrompt(oldValue, newValue, this.serialized.components.objects[currentObjIndex]);
                        }
                        updateLastChange(this.serialized.components.objects[currentObjIndex].props);
                        this.serialized.components.objects[currentObjIndex][valKey] = newValue;
                        if (objects[currentObjIndex]._class === ObjectClass.prompt) {
                            if(this.serialized.client === 'InteractionsCommon'){
                                this.serialized.components.objects[currentObjIndex]['wftName'] = this.serialized.application + "_" + newValue;
                            }
                            renderByRoute(RootRoute.settings);
                        }
                        const treeL = $('#tl_container');
                        treeL.jstree().rename_node(currentID, newValue);
                    }
                    else if (valKey === 'colorClass' || valKey === "iconClass") {
                        this.serialized.components.objects[currentObjIndex].props[valKey] = newValue;
                    }
                    else switch (objects[currentObjIndex]._class) {
                        case ObjectClass.base:
                        case ObjectClass.begin:
                        case ObjectClass.note:
                            break;
                        case ObjectClass.tab:
                            const tabInputs = this.getCustomNodeInputs(parentConfig,contextConfig, true);

                            this.serialized.components.objects[currentObjIndex].props.config = this.resetObjectPropsAndConfig(this.serialized.components.objects[currentObjIndex], context, true);
                            this.serialized.components.objects[currentObjIndex].props.config.inputs = tabInputs;
                            this.serialized.components.objects[currentObjIndex].props.config.matchOrder = tabInputs;
                            updateLastChange(this.serialized.components.objects[currentObjIndex].props);
                            renderByRoute(RootRoute.settings);
                            break;
                        case ObjectClass.custom:

                            const customInputs = this.getCustomNodeInputs(parentConfig,contextConfig);

                            this.serialized.components.objects[currentObjIndex].props.config = this.resetObjectPropsAndConfig(this.serialized.components.objects[currentObjIndex], context, true);
                            this.serialized.components.objects[currentObjIndex].props.config.function.arguments = customInputs;
                            this.serialized.components.objects[currentObjIndex].props.config.inputs = customInputs;
                            updateLastChange(this.serialized.components.objects[currentObjIndex].props);
                            
                            break;
                        case ObjectClass.exit:
                            
                            this.serialized.components.objects[currentObjIndex].props.config = this.resetObjectPropsAndConfig(this.serialized.components.objects[currentObjIndex], context, true);
                            this.serialized.components.objects[currentObjIndex].props.config.outputs = contextConfig.outputs;
                            this.serialized.components.objects[currentObjIndex].props.config.type = contextConfig.type;
                            if(contextConfig.value) {
                                this.serialized.components.objects[currentObjIndex].props.config.value = contextConfig.value;
                            }
                            if(contextConfig.varName) {
                                this.serialized.components.objects[currentObjIndex].props.config.varName = contextConfig.varName;
                            }
                            this.serialized.components.objects[currentObjIndex].props.config[valKey] = newValue;
                            updateLastChange(this.serialized.components.objects[currentObjIndex].props);
                                
                            break;
                        case ObjectClass.data:
                            if (valKey === "script" || valKey === "schema") {
                                this.serialized.components.objects[currentObjIndex].props.config.script = context.props.config.script;
                                this.serialized.components.objects[currentObjIndex].props.config.schema = context.props.config.schema;
                                updateLastChange(this.serialized.components.objects[currentObjIndex].props);
                            }
                            if (valKey !== "inputs") {
                                this.serialized.components.objects[currentObjIndex].props.config[valKey] = context.props.config[valKey];
                                updateLastChange(this.serialized.components.objects[currentObjIndex].props);
                            }
                            if (valKey === "inputs") {
                                this.serialized.components.objects[currentObjIndex].props.config[valKey] = newValue;
                                updateLastChange(this.serialized.components.objects[currentObjIndex].props);
                            }
                            break;
                        default:
                            this.serialized.components.objects[currentObjIndex].props.config = this.resetObjectPropsAndConfig(this.serialized.components.objects[currentObjIndex], context, true);
                            updateLastChange(this.serialized.components.objects[currentObjIndex].props);
                            break;
                    }
                }
        }

        // update instance nodes of application components
        for (var modu = 0; modu < modules.length; modu++) {
            const versions = modules[modu].versions;

            for (var ver = 1; ver <= Object.values(versions).length; ver++) {
                var nodes = versions[ver].flow.nodes;
                switch (context.type) {
                    case "module":
                        var module = _.filter(nodes, { type: context.type });
                        for (var m = 0; m < module.length; m++) {
                            if (module[m].props.parentComponentID === currentID) {
                                var typeIndex = nodes.indexOf(module[m]);
                                if (valKey === 'name') {
                                    updateLastChange(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props);
                                    this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].name = newValue;
                                    if(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props.moduleApp){
                                        this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props.moduleApp = newValue;
                                    }
                                    // TODO: If this module is not in any flows, these two trees will not refresh to show new name
                                    // update application tree
                                    const treeM = $('#tm_container');
                                    const treeL = $('#tl_container');
                                    treeL.jstree().rename_node(currentID, newValue);
                                    treeM.jstree().rename_node(nodes[typeIndex].id, newValue);
                                    if (isTabOpen(this.serialized.components.modules[modu].id)) {
                                        this.postEventToAllViews({ target: "request-refresh", event: { id: nodes[typeIndex].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props, newValue: newValue } });
                                    }
                                } else if (valKey === 'description') {
                                    updateLastChange(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props);
                                    this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props.config.description = newValue;
                                    this.postEventToAllViews({ target: "request-refresh", event: { id: nodes[typeIndex].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props, newValue: newValue } });
                                } else if(valKey === 'version') {
                                    this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props.version = newValue;
                                    this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props.config.version = newValue;
                                    updateLastChange(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props);
                                    
                                    this.postEventToAllViews({ target: "request-refresh", event: { id: nodes[typeIndex].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props, newValue: newValue } });
                                } else if (valKey === 'colorClass' || valKey === "iconClass") {
                                    this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props[valKey] = newValue;
                                    if (isTabOpen(this.serialized.components.modules[modu].id)) {
                                        this.postEventToAllViews({ target: "request-refresh", event: { id: nodes[typeIndex].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndex].props, newValue: newValue } });
                                    }
                                } else {
                                    updateLastChange(context.props);
                                    context.props.config[valKey] = newValue;
                                    if (isTabOpen(this.serialized.components.modules[modu].id)) {
                                        this.postEventToAllViews({ target: "request-refresh", event: { id: context.id, type: "node", upDatedField: valKey, props: context.props, newValue: newValue } });
                                    }
                                }
                            }
                        }
                        break;
                    case "object":
                        var object = _.filter(nodes, { type: context.type, });
                        if (currentID !== undefined) {
                            for (var o = 0; o < object.length; o++) {
                                if (object[o].props.parentComponentID === currentID) {
                                    var typeIndexObj = nodes.indexOf(object[o]);
                                    if (valKey === 'name') {
                                        updateLastChange(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props);
                                        this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj][valKey] = newValue;
                                        
                                        // update application tree
                                        const treeM = $('#tm_container');
                                        const treeL = $('#tl_container');
                                        treeL.jstree().rename_node(currentID, newValue);
                                        treeM.jstree().rename_node(nodes[typeIndexObj].id, newValue);
                                        if (isTabOpen(this.serialized.components.modules[modu].id)) {
                                            this.postEventToAllViews({ target: "request-refresh", event: { id: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props, newValue: newValue } });
                                        }
                                    } else if (valKey === 'description') {
                                        updateLastChange(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props);
                                        this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config[valKey] = newValue;
                                        this.postEventToAllViews({ target: "request-refresh", event: { id: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props, newValue: newValue } });

                                    } else if (valKey === 'colorClass' || valKey === "iconClass") {
                                        this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props[valKey] = newValue;
                                        if (isTabOpen(this.serialized.components.modules[modu].id)) {
                                            this.postEventToAllViews({ target: "request-refresh", event: { id: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props, newValue: newValue } });
                                        }
                                    } else if (context.props.nodeTypeName !== NodeTypeName.data) {
                                        //  Save original configs for Custom Node inputs before resetObjectPropsAndConfig() is called - 1019
                                        const contextConfig = _.cloneDeep(context.props.config);
                                        const flowNodeConfig = _.cloneDeep(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config);

                                        // dont over-write the originating node's props, etc... 
                                        const shouldResetDisabledPropsAndConfig = this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].id !== context.id;

                                        this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config = (shouldResetDisabledPropsAndConfig) ?
                                            this.resetObjectPropsAndConfig(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj], context, false) :
                                            context.props.config;
                                            updateLastChange(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props);
                                            
                                        //  Insert new Arguments/Inputs into Configs for Custom Node inputs after resetObjectPropsAndConfig() has been called - 1019
                                        if(context.props.nodeTypeName === NodeTypeName.custom || context.props.nodeTypeName === NodeTypeName.tab) {
                                            const contextId = context.id;
                                            const flowNodeId = this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].id;
                                            const isTabularRouter = context.props.nodeTypeName === NodeTypeName.tab;
                                            const isContextNode = contextId === flowNodeId || isTabularRouter;

                                            // do not update via currentId as that was already updated in updateContext    
                                            const newArgs = this.getCustomNodeInputs(flowNodeConfig,contextConfig, isContextNode);
                                            
                                            if(context.props.nodeTypeName === NodeTypeName.custom) {
                                                this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.function.arguments = newArgs;
                                            }
                                            if(context.props.nodeTypeName === NodeTypeName.tab) {
                                                this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.matchOrder = newArgs;
                                            }
                                            this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.inputs = newArgs;
                                        
                                        }

                                        //  Insert new Output into Configs for Exit Node after resetObjectPropsAndConfig() has been called - 1019
                                        if(context.props.nodeTypeName === NodeTypeName.exit) {
                                            this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.outputs = contextConfig.outputs;
                                            this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.type = contextConfig.type;
                                           
                                            if(contextConfig.value) {
                                                this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.value = contextConfig.value;
                                            }
                                            if(contextConfig.varName) {
                                                this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.varName = contextConfig.varName;
                                            }
                                            this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config[valKey] = newValue;
                                        }
                                        if (isTabOpen(this.serialized.components.modules[modu].id)) {
                                            this.postEventToAllViews({ target: "request-refresh", event: { id: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props, newValue: newValue } });
                                        }
                                    } else if (context.props.nodeTypeName === NodeTypeName.data && valKey !== "inputs") {
                                        if (valKey === "script" || valKey === "schema") {
                                            this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.script = context.props.config.script;
                                            this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config.schema = context.props.config.schema;
                                        }
                                        this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config[valKey] = context.props.config[valKey];
                                        updateLastChange(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props);
                                        if (isTabOpen(this.serialized.components.modules[modu].id)) {
                                            this.postEventToAllViews({ target: "request-refresh", event: { id: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props, newValue: newValue } });
                                        }
                                    } else if (context.props.nodeTypeName === NodeTypeName.data && valKey === "inputs" && this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.parentComponentID === currentID) {
                                        this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props.config[valKey] = context.props.config[valKey];
                                        updateLastChange(this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props);
                                        if (isTabOpen(this.serialized.components.modules[modu].id)) {
                                            this.postEventToAllViews({ target: "request-refresh", event: { id: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].id, type: "node", upDatedField: valKey, props: this.serialized.components.modules[modu].versions[ver].flow.nodes[typeIndexObj].props, newValue: newValue } });
                                        }
                                    }
                                }
                            }
                        }
                        break;
                }
            }
            
            this.setSerializedChanged();
            // this fixes the 'dancing member tree' glitch DEVDES2-1285
            // this.postEventToAllViews({ target: "request-update-member" });
        }
            
    }

    updateCommonModulePromptNodes = (context, flowVersion, nodes, key, value) => {
        // CM TODO: was unable to access `key` inside of following nodes.forEach
        if(key === 'en-US'){
            nodes.forEach((node) => {
                // CM TODO: handle disabledInFlow
                // const shouldResetDisabledPropsAndConfig = nodes.id !== context.id;
                if(node.props.nodeTypeName === NodeTypeName.prompt && node.props.parentComponentID === context.props.parentComponentID){ 
                    node.props.config.text = {'en-US': value};
                    node.props.lastLocalChange = window.apm.timestamp;
                    // CM TODO: because postEventToAllViews is asynchronous...
                    this.postEventToAllViews({ target: "request-refresh", 
                        event: { 
                            id: node.id, 
                            type: "node",
                            flowVersion: flowVersion, 
                            props: node.props, 
                            newValue: node.props.config.text } });
                }
            });
        }
    }

    /**
     * Handles prompt text changes and CM alias changes
     * find nodes under module's versions index
     * update their config data
     * @param {*} context 
     * @param {*} key 
     * @param {*} value 
     */
    updateCommonModuleNodes(context, key, value){
        // CM TODO: move this logic to function
        const pcm = (window.apm.getCurrentDiagramWidget().props.node.props.isRootModule) ? window.apm.getCurrentDiagramWidget().props.node 
            : window.apm.serialized.components.modules.find(mod => mod.id === window.apm.getCurrentDiagramWidget().props.node.props.moduleId) ;
        const commonModules = window.apm.serialized.components.modules.filter(mod => mod.props.moduleId === pcm.id);
        // const flowVersion = pcm.version;    
        const flowVersion = context.props.flowVersion || 1;    

        if(context.props.nodeTypeName === NodeTypeName.prompt){
            // CM TODO: was unable to access `key` inside of following nodes.forEach
            if(key === 'en-US'){
                commonModules.forEach(mod => {
                    const flow = mod.versions[flowVersion].flow;
                    const nodes = flow.nodes;
                    
                    this.updateCommonModulePromptNodes(context, flowVersion, nodes, key, value);
                });
            }
        }

        this.setSerializedChanged();                   
    }

    /** Clean up default value and return it. */
    getDefaultValueFromLocalVariable(localVariable) {
        const variableConfig = localVariable.props.config;
        // Clean up typo property.
        if ('defalutValue' in variableConfig) {
            variableConfig.defaultValue ??= variableConfig.defalutValue;
            delete variableConfig.defalutValue;
            localVariable.props.lastLocalChange = window.apm.timestamp;
        }

        const { defaultValue, type } = variableConfig;
        switch (type) {
            case VariableType.boolean:
                return (typeof defaultValue === 'boolean') ? defaultValue : (
                    defaultValue === 'true'
                );
            case VariableType.number:
                return (typeof defaultValue === 'number') ? defaultValue : 0;
        }

        return defaultValue ?? '';
    }

    /**
     * Add local variable to inputs on modules, nodes, & global intent mapping.
     *
     * @param {Design} serialized design to add a module input to.
     */
    addModuleInput(localVariable, moduleId, serialized) {
        const { sensitiveData = false, varName } = localVariable.props.config;
        const argumentName = `mod.${varName}`;
        const updateInputItem = (item) => {
            let { inputs = [] } = item.props.config;
            const priorCount = inputs.length;
            if (!inputs.some((input) => {
                return input.argumentName === argumentName;
            })) {
                const value = this.getDefaultValueFromLocalVariable(localVariable);
                inputs = [...inputs, {
                    argumentName,
                    defaultValue: value,
                    id: createUuid(),
                    sensitiveData,
                    type: ValueOrVariableType.value,
                    value,
                    varName: null
                }].sort((inputA, inputB) => {
                    return inputA.argumentName.localeCompare(inputB.argumentName);
                });
            }
            item.props.config.inputs = inputs;
            return priorCount !== inputs.length;
        };
        this._itterateItemsWithInputs(updateInputItem, moduleId, serialized);
        document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        return serialized;
    }

    /**
     * Remove local variable from inputs on modules, nodes, & global intent mapping.
     *
     * @param {Design} serialized design to remove a module input from.
     */
    deleteModuleInput(localVariable, moduleId, serialized) {
        const argumentName = `mod.${localVariable.props.config.varName}`;
        const updateInputItem = (item) => {
            let { inputs = [] } = item.props.config;
            const priorCount = inputs.length;
            inputs = inputs.filter((input) => {
                return input.argumentName !== argumentName;
            });
            item.props.config.inputs = inputs;
            return priorCount !== inputs.length;
        };

        this._itterateItemsWithInputs(updateInputItem, moduleId, serialized);
        return serialized;
    }

/*
remove local variable references when local variable is deleted
*/
    removeLocalVariableReferences(module, localVariable, lvUsedIn) {
        console.log(lvUsedIn)
        lvUsedIn.forEach(usedIn => {
            if (usedIn.type === "inFlowNode") {
                const nodeIndex = module.versions[1].flow.nodes.findIndex(n => n.id === usedIn.node.id);
                if (nodeIndex !== -1) {
                    const inputs = module.versions[1].flow.nodes[nodeIndex].props.config.inputs || [];
                    const outputs = module.versions[1].flow.nodes[nodeIndex].props.config.outputs || [];
                    outputs.forEach((output, index) => {
                        if (output.varName === `mod.${localVariable.name}`) {
                            module.versions[1].flow.nodes[nodeIndex].props.config.outputs.splice(index, 1);
                        }
                    })
                    inputs.forEach((input, index) => {
                        if (input.varName === `mod.${localVariable.name}`) {
                            module.versions[1].flow.nodes[nodeIndex].props.config.inputs.splice(index, 1);
                        }
                    })
                }
            }
            if(usedIn.type==="inFlowLink"){
                const linkIndex = module.versions[1].flow.links.findIndex(n => n.id === usedIn.link.id);
                if (linkIndex !== -1) {
                    const inputs = module.versions[1].flow.links[linkIndex].props.config.inputs || [];
                    inputs.forEach((input, index) => {
                        if (input.varName === `mod.${localVariable.name}`) {
                            module.versions[1].flow.links[linkIndex].props.config.inputs.splice(index, 1);
                        }
                    })
                } 
            }
        })
    }
    /* Update a component node with ID
     * @param {string} nodeId
     * @param {Object|string} node Node serialized model | module reference ID
     */
    setNode(nodeId, node) {
        if (!nodeId) {
            return null;
        }
        const idFields = nodeId.split("_"); // 0: parent node id; 1: element id
        if (idFields.length === 2 && typeof node === "string") {
            var ref = node;
            node = this.getNode(idFields[0]);
            var elemIndex = node.versions[node.version].flow.nodes.findIndex(element => element.id === idFields[1]);
            var elem = node.versions[node.version].flow.nodes[elemIndex];
            elem.props.ref = ref;
            this.setSerializedChanged();
            return;
        }
        const nodeIndex = this.serialized.components[node.type + "s"].findIndex(element => element.id === nodeId);
        var skipPermission = false;
        if (nodeIndex !== -1) {
            const oldVers = JSON.stringify(this.serialized.components[node.type + "s"][nodeIndex].versions);
            const newVers = JSON.stringify(node.versions);
            if (oldVers === newVers) {
                skipPermission = true;
            }
        }
        if (!this.hasPermission(nodeId, "write") && !skipPermission) {
            this.handleNoWritePermission();
            return;
        }
        delete node.x;
        delete node.y;

        if (nodeIndex !== -1) {
            this.serialized.components[node.type + "s"][nodeIndex] = node;
        } else {
            this.serialized.components[node.type + "s"].push(node);
        }
        this.setSerializedChanged();
    }

    getVariables() {
        var varOptions = [];
        var variables = this.serialized.components["variables"];
        for (var i = 0; i < variables.length; i++) {
            varOptions.push({ value: variables[i].id, name: variables[i].name });
        }
        return varOptions;
    }

    //add localVariables as inputs when adding module to globalIntent
    // this is actually setting localVariables on a module
    getModuleInputs(module) {
        const localVariables = module.localVariables.filter(lv => lv.props.config.moduleInput);
        let inputs = [];
        if (localVariables && localVariables.length > 0) {
            localVariables.forEach((localVariable, idx) => {
               inputs.push ( {
                    argumentName:`mod.${localVariable.props.config.varName}`,
                    type: "value",
                    id: _.get(module, ['props', 'config', 'inputs', idx,'id'], createUuid()),
                    value: localVariable.props.config.defaultValue,
                    defaultValue: localVariable.props.config.defaultValue,
                    varName: null,
                    sensitiveData: localVariable.props.config.sensitiveData ? localVariable.props.config.sensitiveData : false
               })
                localVariable.props.lastLocalChange = window.apm.timestamp;
            })
        }
        return inputs;
    }
    
    //update defaulf value of module Inputs 
    updateDefaultValue(variable, oldValue, newValue,parentModuleId) {
        
        const mod = window.apm.serialized.components.modules.find(mod => mod.id === parentModuleId);
        
        if (mod) {
            const moduleUsedIn=ModuleUsedIn.getModuleUsedIn(mod, window.apm.serialized);
            const usedInFlows = moduleUsedIn.usedInFlows
            usedInFlows.forEach(mo => {
                const usedInModuleIndex = window.apm.serialized.components.modules.findIndex(m => m.id === mo);
                if (usedInModuleIndex !== -1) {
                    const usedInModule = this.getNode(window.apm.serialized.components.modules[usedInModuleIndex].id);
                    const nodes = usedInModule.versions[1].flow.nodes;
                    
                    nodes.forEach((node) => {
                        if (node.props.parentComponentID === parentModuleId&&node.props.config.inputs) {
                            const inputs = node.props.config.inputs;
                            
                            inputs.map((input) => {
                                const variableName = input.argumentName.substring(4, input.argumentName.length);
                                if (variable.props.config.name === variableName) {
                                        if(input.value ===  oldValue){
                                            input.value = newValue;
                                        }
                                        
                                        input.defaultValue = newValue;
                                        node.props.lastLocalChange = this.timestamp;
                                        this.postEventToAllViews({ target: "request-refresh", event: { id: node.id, type: "node", upDatedField: "inputs", props: node.props, newValue: inputs } });
                                    }
   
                            });
                        }
                    })
                }
            })
            const usedInGlobalIntents = moduleUsedIn.usedInGlobalIntents;
            usedInGlobalIntents.map((globalIntent, i) => {
                const inputs = globalIntent.moduleProperties.props.config.inputs;
                inputs.map((input, i) => {
                    const variableName = input.argumentName.substring(4, input.argumentName.length);
                    if (variable.props.config.name === variableName&&input.type==="value") {
                        if (input.value === oldValue) {
                            input.value = newValue;
                            input.defaultValue = newValue;
                        }
                    }
                })
            })
            // this.reactDOMComponents.render({ name: 'IntentsWidget', props: { serialized: window.apm.serialized }, $el: $('#intents') });
        }
    }

   //upate the application variable sensitivedata if they are used in inputs and outputs 
    updateSensitiveData(id, name, newValue) {
        const { isTabOpen } = useCanvasTabs.getState();
        window.apm.serialized.components.modules.forEach(module => {
            if (module.props.config.inputs) {
                module.props.config.inputs.forEach(input => {
                    if (input.type === "variable") {
                        const varName =input.varName.split(".")[1];
                        if (varName === name) {
                           input.sensitiveData = newValue;
                            module.props.lastLocalChange = window.apm.timestamp;
                        }
                    }
                })
            }
            if (module.props.config.outputs) {
                module.props.config.outputs.forEach(output => {
                    if (output.type !== "VALUE") {
                        const varName = output.varName.split(".")[1];
                        if (varName === name) {
                            output.sensitiveData = newValue;
                            module.props.lastLocalChange = window.apm.timestamp;
                        }
                    }
                })
            }
            module.versions[1].flow.nodes.forEach(node => {
                if (node.props.config.inputs) {
                    node.props.config.inputs.forEach(input => {
                        if (input.type === "variable"&&input.varName) {
                            const varName = input.varName.split(".")[1];
                            if (varName === name) {
                                input.sensitiveData = newValue;
                                if (node.props.nodeTypeName === NodeTypeName.custom) {
                                    node.props.config.function.arguments = node.props.config.inputs;
                                } else if (node.props.nodeTypeName === NodeTypeName.prompt) {
                                    if (isTabOpen(module.id)) {
                                        this.postEventToAllViews({ target: "request-refresh", event: { id: node.id, type: "node", upDatedField: "sensitiveData", props: node.props, newValue: newValue } });
                                    }
                                }
                                node.props.lastLocalChange = window.apm.timestamp;
                            }
                        }
                    })
                }
                if (node.props.config.matchOrder) {
                    node.props.config.matchOrder.forEach(match => {
                        if (match.type === "variable") {
                            const varName = match.varName.split(".")[1];
                            if (varName === name) {
                                match.sensitiveData = newValue;
                                node.props.lastLocalChange = window.apm.timestamp;
                            }
                        }
                    })
                }

                if (node.props.config.outputs) {
                    node.props.config.outputs.forEach(output => {
                        if (output.type !== "VALUE" && output.varName) {
                            const varName = output.varName.split(".")[1];
                            if (varName === name) {
                                output.sensitiveData = newValue;
                                if (node.props.nodeTypeName === NodeTypeName.custom) {
                                   node.props.config.function.outputs = node.props.config.outputs;
                                }
                                node.props.lastLocalChange = window.apm.timestamp;

                            }
                        }
                    })
                    if (node.props.nodeTypeName === NodeTypeName.tab) {
                        node.props.config.outputTo.forEach(output => {
                            if (output.type !== "VALUE" && output.varName) {
                                const varName = output.varName.split(".")[1];
                                if (varName === name) {
                                    output.sensitiveData = newValue;
                                    node.props.lastLocalChange = window.apm.timestamp;
                                }
                            }
                        })
                    }
                }
            }

            );
        });
        window.apm.serialized.components.objects.forEach(object => {
            if (object.props.config.inputs) {
                object.props.config.inputs.forEach(input => {
                    if (input.type === "variable") {
                        const varName = input.varName.split(".")[1];
                        if (varName === name) {
                            input.sensitiveData = newValue;
                            if (object.props.nodeTypeName === NodeTypeName.custom) {
                                object.props.config.function.arguments = object.props.config.inputs;
                            }
                            object.props.lastLocalChange = window.apm.timestamp;
                        }
                    }
                })
            }
            if (object.props.config.outputs) {
                object.props.config.outputs.forEach(output => {
                    if (output.type !== "VALUE" && output.varName) {
                        const varName = output.varName.split(".")[1];
                        if (varName === name) {
                            output.sensitiveData = newValue;
                            if (object.props.nodeTypeName === NodeTypeName.custom) {
                                object.props.config.function.outputs = object.props.config.outputs;
                            }
                            object.props.lastLocalChange = window.apm.timestamp;
                        }
                    }
                })
                if (object.props.nodeTypeName === NodeTypeName.tab) {
                    object.props.config.outputTo.forEach(output => {
                        if (output.type !== "VALUE" && output.varName) {
                            const varName = output.varName.split(".")[1];
                            if (varName === name) {
                                output.sensitiveData = newValue;
                                object.props.lastLocalChange = window.apm.timestamp;
                            }
                        }
                    })
                }
            }
        });
        this.setSerializedChanged();
    }

    /**
     * Add a component node and update its timestamps
     * @param {Object} node Node model
     * @param {boolean} renderViews option to skip rerendering for non-canvas events, default is `true`
     */
    addNode(node, renderViews = true) {
        const timestamp = window.apm.timestamp;
        const { isOmniChannel } = window.apm.serialized.omniChannel;

        delete node.props.lastSavedChange;
        delete node.props.timeAddedToFlow;

        node.props.createdTime = timestamp;
        node.props.lastLocalChange = timestamp;

        // application-level module nodes 
        if (node.type === 'module') {
            delete node.x;
            delete node.y;

            // update timestamps on new flow: links and nodes
            node.versions[1].flow.links.forEach(l => {
                delete l.props.lastSavedChange;
                delete l.props.sourceChangedTime;
                delete l.props.targetChangedTime;
                delete l.props.updatedTime;
                l.props.lastLocalChange = timestamp;
            });

            node.versions[1].flow.nodes.forEach(n => {
                delete n.props.lastSavedChange;
                n.props.timeAddedToFlow = timestamp;
            });

            this.serialized.components[node.type + "s"].push(node);

        }

        // add to folder

        if (node.type === 'folder') {
            this.serialized.folders[node.props.folderType + "s"].push(node);
        }

        // application-level
        if (node.type === "variable" || node.type === "object" || node.type === "intent") {
            if(node._class === ObjectClass.prompt) {
                // add channel to prompt config
                const existingPromptLanguages = Object.values(window.apm.serialized.languages).map(lang => {return lang.language});

                existingPromptLanguages.forEach(lang => {
                    if(!node.props.config.text.hasOwnProperty(lang)|| node.props.config.text[lang] === undefined){
                        node.props.config.text[lang] = "";
                    }
                    
                })
                if(isOmniChannel){
                    node = window.apm.knowledgeBase.initializePromptChannelConfig(node);
                }
            }
            delete node.x;
            delete node.y;
            if (node.type === DesignItemType.object) {
                normalizeObjectOrNode(node, this.serialized);
            }
            this.serialized.components[node.type + "s"].push(node);
        }

        if (node.type === "variable") {
            document.dispatchEvent(new CustomEvent("ev-dialog-variable-changed"));
        }

        // flow items
        else {
            node.props.timeAddedToFlow = timestamp;
        }

        this.setSerializedChanged();

        // 
        if(renderViews){
            // update settings props
            renderByRoute(RootRoute.settings);
            //Authoring mode for Common Modules doesn't have IntentsWidge - DEVDES2-1265            
            if(!this.isCommonModule()){
                renderByRoute(RootRoute.intents);
            }

        }

    }

    addUnitTest(unitTest) {
        unitTest.createdTime = window.apm.timestamp;
        unitTest.lastLocalChange = window.apm.timestamp;
        window.apm.serialized.unitTests.push(unitTest);
        window.apm.setSerializedChanged();
    }

    renameUnitTest(id, newName) {
        const existing = window.apm.serialized.unitTests.find(ut => ut.id === id);
        if (existing) {
            existing.name = newName;
            existing.lastLocalChange = window.apm.timestamp;
            window.apm.setSerializedChanged();
        }
    }

    removeUnitTest(id) {
        const idx = window.apm.serialized.unitTests.findIndex(ut => ut.id === id);
        if (idx > -1) {
            window.apm.serialized.unitTests.splice(idx, 1);
            window.apm.setSerializedChanged();
        }
    }

    /** Initialize display preferences in UI. */
    initDisplayPrefs() {
        const treeContainer = document.getElementById('tl_container');
        const toggleDisabledElementsButton = document.getElementById('icon-toggle-disabled-elements');
        const { hideDisabledElements } = this.serialized.displayPrefs;
        setElementClassEnabled(treeContainer, 'hideDisabled', hideDisabledElements);
        setElementClassEnabled(toggleDisabledElementsButton, 'hideDisabled', hideDisabledElements);
    }

    /**
     * 
     * @param {Object} mod 
     * @param {string} type 
     * @param {} itemId 
     */
    updateDeletedModulesMap(mod, type, itemId) {
        switch (type) {
            case 'links':
                window.apm.serialized.deletedMap = window.apm.serialized.deletedMap || {};
                window.apm.serialized.deletedMap['modules'] = window.apm.serialized.deletedMap['modules'] || {};
                window.apm.serialized.deletedMap['modules'][mod.id] = window.apm.serialized.deletedMap['modules'][mod.id] || {};
                window.apm.serialized.deletedMap['modules'][mod.id][type] = window.apm.serialized.deletedMap['modules'][mod.id]['links'] || [];
                window.apm.serialized.deletedMap['modules'][mod.id][type].push(itemId);
                break;
            case 'nodes':
                window.apm.serialized.deletedMap = window.apm.serialized.deletedMap || {};
                window.apm.serialized.deletedMap['modules'] = window.apm.serialized.deletedMap['modules'] || {};
                window.apm.serialized.deletedMap['modules'][mod.id] = window.apm.serialized.deletedMap['modules'][mod.id] || {};
                window.apm.serialized.deletedMap['modules'][mod.id]['nodes'] = window.apm.serialized.deletedMap['modules'][mod.id]['nodes'] || [];
                window.apm.serialized.deletedMap['modules'][mod.id]['nodes'].push(itemId);
                break;
            case 'localVariables':
                window.apm.serialized.deletedMap = window.apm.serialized.deletedMap || {};
                window.apm.serialized.deletedMap['modules'] = window.apm.serialized.deletedMap['modules'] || {};
                window.apm.serialized.deletedMap['modules'][mod.id] = window.apm.serialized.deletedMap['modules'][mod.id] || {};
                window.apm.serialized.deletedMap['modules'][mod.id]['localVariables'] = window.apm.serialized.deletedMap['modules'][mod.id]['localVariables'] || [];
                window.apm.serialized.deletedMap['modules'][mod.id]['localVariables'].push(itemId);
                break;

        }

    }

    updateDeletedApplicationObjectsMap(type, nodeId) {
        this.serialized.deletedMap = this.serialized.deletedMap || {};
        this.serialized.deletedMap['application-' + type] = this.serialized.deletedMap['application-' + type] || [];
        this.serialized.deletedMap['application-' + type].push(nodeId);

    }

    updateDeletedGlobalIntentsMap(id) {
        this.serialized.deletedMap = this.serialized.deletedMap || {};
        this.serialized.deletedMap['globalIntents'] = this.serialized.deletedMap['globalIntents'] || [];
        this.serialized.deletedMap['globalIntents'].push(id)
    }
    updateDeletedCompoundIntentsMap(id) {
        this.serialized.deletedMap = this.serialized.deletedMap || {};
        this.serialized.deletedMap['compoundIntents'] = this.serialized.deletedMap['compoundIntents'] || [];
        this.serialized.deletedMap['compoundIntents'].push(id)
    }
    updateDeletedCsvDataMap(id) {
        this.serialized.deletedMap = this.serialized.deletedMap || {};
        this.serialized.deletedMap['csvData'] = this.serialized.deletedMap['csvData'] || [];
        this.serialized.deletedMap['csvData'].push(id)
    }

    updateDeletedGqlSchemaMap(id) {
        this.serialized.deletedMap = this.serialized.deletedMap || {};
        this.serialized.deletedMap['gqlSchema'] = this.serialized.deletedMap['gqlSchema'] || [];
        this.serialized.deletedMap['gqlSchema'].push(id)
    }

    updateDeletedFoldersMap(id) {
        this.serialized.deletedMap = this.serialized.deletedMap || {};
        this.serialized.deletedMap['folders'] = this.serialized.deletedMap['folders'] || [];
        this.serialized.deletedMap['folders'].push(id)
    }

    /**
     * Remove a component node with ID
     * @param {string} nodeId
     */
    removeNode(nodeId, noRedraw = false) {
        for (const type in this.serialized.components) {
            if (this.serialized.components.hasOwnProperty(type)) {
                for (var i = 0; i < this.serialized.components[type].length; i++) {
                    if (nodeId === this.serialized.components[type][i].id) {
                        // update deletedMaps
                        this.updateDeletedApplicationObjectsMap(type, nodeId);

                        // splice object from collection
                        this.serialized.components[type].splice(i, 1);
                        i--;
                    }
                }
            }
        }

        // remove module contents from usedIn modules
        for (let mod = 0; mod <this.serialized.components.modules.length; mod++) {
            let module = this.serialized.components.modules[mod];
        
            for (let nod = 0; nod < module.versions[1].flow.nodes.length; nod++) {
                const node = module.versions[1].flow.nodes[nod];
                if (node.props.parentComponentID && node.props.parentComponentID === nodeId) {
                    const links = module.versions[1].flow.links;
                    for (let lin = 0; lin < links.length; lin++) {
                        if (node.id === links[lin].source || node.id === links[lin].target) {
                            // update deletedMaps
                            this.updateDeletedModulesMap(module, 'links', links[lin].id);
                            // splice link from collection
                            module.versions[1].flow.links.splice(lin, 1);
                            lin--;
                        }
                    }
                    // update deletedMaps
                    this.updateDeletedModulesMap(module, 'nodes', node.id);
                    module.versions[1].flow.nodes.splice(nod, 1);
                    nod--;
                }
                
            }
           
        }
        if(!noRedraw) {
            this.postEventToAllViews({ target: "request-reload-data-model" });
        }
       
        this.setSerializedChanged();

        // update settings props
        renderByRoute(RootRoute.settings);
        renderByRoute(RootRoute.intents);
    }

    /**
     * Remove validation errors on nodes/links within a module.
     * @param {string} moduleId
     */
    removeValidationError(moduleId) {
        const { modules } = window.apm.serialized.components;
        const mod = modules.find((mod) => mod.id === moduleId);
        if (!mod) {
            return;
        }
        /** @param {DesignModuleLink | DesignModuleNode} linkOrNode */
        const removeErrorFromItem = (linkOrNode) => {
            if (Constant.k.VALIDATION_ERROR in (linkOrNode.props.error ?? {})) {
                delete linkOrNode.props.error[Constant.k.VALIDATION_ERROR];
            }
        };

        const { links, nodes } = mod.versions[1].flow;
        links.forEach(removeErrorFromItem);
        nodes.forEach(removeErrorFromItem);
    }

    /**
     * Rename a component node with ID; also update treeview for new name
     * @param {string} nodeId
     * @param {string} newName
     * @param {jQuery} treeDom Treeview reference where the node is renamed
     */
    renameNode(nodeId, newName, treeDom = null) {
        let node = window.apm.getNode(nodeId);

        if (node.type !== "variable") { 
            let name = newName.toUpperCase().replace(/ /g, "_");
            // rename serialized
            for (const type in this.serialized.components) {
                if (this.serialized.components.hasOwnProperty(type)) {
                    for (var i = 0; i < this.serialized.components[type].length; i++) {
                        if (nodeId === this.serialized.components[type][i].id) {
                            this.serialized.components[type][i].name = name;
                            this.updateParentNode(this.serialized.components[type][i], this.serialized.components[type][i].id, "name", name, type);
                        }
                    }
                }
            }
        }
        else {
            for (var i = 0; i < this.serialized.components['variables'].length; i++) {
                if (nodeId === this.serialized.components['variables'][i].id) {
                    this.serialized.components['variables'][i].name = newName;
                    this.updateParentNode(this.serialized.components['variables'][i], this.serialized.components['variables'][i].id, "name", newName, 'variables');
                }
            }
        }

        // rename tree
        if (treeDom) {
            treeDom.jstree().rename_node(nodeId, newName);
        }

        // rename tab title (if opened)
        useCanvasTabs.getState().renameTab(nodeId);

        this.setSerializedChanged();

        // update settings props
        renderByRoute(RootRoute.settings);
        renderByRoute(RootRoute.intents);
    }

    /**
         * Update a module description 
         * @param {string} nodeId
         * @param {Object|string} node Node serialized model | module reference ID
         */
    updateModuleDescription(module) {
        const currentModuleIndex = window.apm.serialized.components.modules.findIndex(mo => mo.id === window.apm.getCurrentDiagramWidget().props.node.id);
        if (currentModuleIndex !== -1) {
            const moduleInstanceIndex = window.apm.serialized.components.modules[currentModuleIndex].versions[1].flow.nodes.findIndex(no => no.props.parentComponentID === module.id);
            if (moduleInstanceIndex !== -1) {
                window.apm.serialized.components.modules[currentModuleIndex].versions[1].flow.nodes[moduleInstanceIndex].props.config.description = module.props.config.description;
                updateLastChange(window.apm.serialized.components.modules[currentModuleIndex].versions[1].flow.nodes[moduleInstanceIndex].props);
            }
            this.postEventToCurrentView({ target: "request-refresh", event: { id: window.apm.serialized.components.modules[currentModuleIndex].versions[1].flow.nodes[moduleInstanceIndex].id, type: "node", upDatedField: "description", props: window.apm.serialized.components.modules[currentModuleIndex].versions[1].flow.nodes[moduleInstanceIndex].props, newValue: module.props.config.description } });
        }
    }

    /**
     * Get a variable.
     *
     * @param {string} varIdOrName to get.
     * @param {string|null} parentModuleId If local variable, use module id. If global variable, use null.
     * @param {Design|null} serialized design to get the value off from.
     * @returns {*}
     */
    getVariable(varIdOrName, parentModuleId, serialized = null) {
        return Utility.getVariable(varIdOrName, parentModuleId, serialized);
    }

    /**
     * Refactor a variable
     * @param {string} varIdOrName
     * @param {string|null} parentModuleId If local variable, use module id. If global variable, use null
     * @param {Object|null} props New properties
     * @returns {*}
     */
    refactorVariable(varModel, parentModuleId = null, props = null, usedIn = null, oldName) {
        if (!props) {
            return;
        }
        var changed = false;
        
        if (props.varName && props.varName !== oldName) {
            const scopedOldName = parentModuleId ? Constant.k.SCOPE_MODULE + "." + oldName : Constant.k.SCOPE_APPLICATION + "." + oldName;
            const scopedNewName = parentModuleId ? Constant.k.SCOPE_MODULE + "." + props.varName : Constant.k.SCOPE_APPLICATION + "." + props.varName;
            varModel.props.config.varName = props.varName;
            varModel.name = varModel.props.config.varName + (varModel.props.config.alias ? " (" + varModel.props.config.alias + ")" : "");

            this.refactorVariableNameinGlobalIntents(scopedOldName, scopedNewName, window.apm.getCurrentDiagramWidget().props.node.id);

            if(parentModuleId) {
                const parentModule = window.apm.serialized.components.modules.find(mod => mod.id === parentModuleId);
                this.refactorVariableName(parentModule, scopedOldName, scopedNewName);
                parentModule.versions[1].flow.links.forEach(link => {
                    this.refactorVariableName(link, scopedOldName, scopedNewName);
                });
                parentModule.versions[1].flow.nodes.forEach(node => {
                    if (node._class !== ObjectClass.begin) {
                        this.refactorVariableName(node, scopedOldName, scopedNewName);
                    }
                });
            } else {
                
                usedIn.forEach(ui => {
                    const node = ui.node || ui.obj || ui.link || ui;
                    this.refactorVariableName(node, scopedOldName, scopedNewName)
                });
            }

            // }
            const treeDom = $("#tl_container");
            if (treeDom) {
                treeDom.jstree().rename_node(varModel.id, varModel.name);
            }
            changed = true;
            
        }
        if (changed) {
            this.setSerializedChanged();
            //this.postEventToAllViews({ target: "request-reload-data-model", type: "variable" });
        }
       
        renderByRoute(RootRoute.intents);
        renderByRoute(RootRoute.settings);
        this.postEventToAllViews({ target: "request-reload-data-model" });
        Utility.closeApplicationOverlay();
    }

    /**
     * Refactor variable name given node
     * @param {Object} node
     * @param {string} scopedOldName
     * @param {string} scopedNewName
     * @param {string|null} parentModuleId
     */
    refactorVariableName(node, scopedOldName, scopedNewName, parentModuleId = null, varId = null) {
        const oldScriptName = scopedOldName.split(".")[1];
        const newScriptName = scopedNewName.split(".")[1];
        const oldNamePatternScript = new RegExp(`\\b${oldScriptName}\\b`, 'g');

        if(node.appProp){
            node.appProp.variable = scopedNewName;
            this.setAppProperty(node.appProp);
            return;
        }
        if(node.func){
            if(node.func.parameters){
                node.func.parameters.forEach(param => {
                    if(param.value === scopedOldName){
                      param.value = scopedNewName;
                    }
                });
            }
            if(node.func.script){
                node.func.script = node.func.script.replace(oldNamePatternScript, newScriptName);
            }
            return;
        }
        
        if (!node.props || !node.props.config) {
            return;
        }

        const nodeProps = node.props;
        const nodeConfig = nodeProps.config;
        const nodeType = node.type;
        const nodeId = node.props.parentComponentID || node.id;

        //Update variable name reference in all inputs & outputs
        
        const objectInputs = nodeConfig.inputs;

        if (objectInputs) {
            objectInputs.forEach(input => {
                if (input.varName && input.varName === scopedOldName) {
                    if (parentModuleId !== null && input.id === varId) input.varName = scopedNewName;
                    if (parentModuleId === null) input.varName = scopedNewName;
                }
                if (input.argumentName && input.argumentName === scopedOldName) {
                    if (parentModuleId !== null && input.id === varId) input.argumentName = scopedNewName;
                    if (parentModuleId === null) input.argumentName = scopedNewName;
                }
            });
            node.props.config.inputs = objectInputs;
            this.updateParentNode(node, nodeId, "inputs", node.props.config.inputs, nodeType);
        }
        const objectOutputs = nodeConfig.outputs;

        if (objectOutputs) {
            objectOutputs.forEach(output => {
                if (output.varName && output.varName === scopedOldName) {
                    if (parentModuleId !== null && output.id === varId) output.varName = scopedNewName;
                    if (parentModuleId === null) output.varName = scopedNewName;
                }
                if (output.argumentName && output.argumentName === scopedOldName) {
                    if (parentModuleId !== null && output.id === varId) output.argumentName = scopedNewName;
                    if (parentModuleId === null) output.argumentName = scopedNewName;
                }
            });
            node.props.config.outputs = objectOutputs;
            this.updateParentNode(node, nodeId, "outputs", node.props.config.outputs, nodeType);
        }

        switch(node._class) {
            //Update variable name reference in link conditions
            case DesignClass.link: {
                const condition = nodeConfig.condition;
                const serializedCondition = nodeConfig.serializedCondition
                if (condition) {
                    condition.forEach((cond,condI) => {
                        cond.forEach((c,cI) => {
                            node.props.config.condition[condI][cI] = c.replace(scopedOldName, scopedNewName);
                        });
                    });
                }
                if(serializedCondition){
                    node.props.config.serializedCondition = nodeConfig.serializedCondition.replace(scopedOldName, scopedNewName);
                }
                window.apm.postEventToCurrentView({ target: "request-refresh", event: { id: node.id, type: "link", updatedLink: node } });
                break;
            }
            // Update variable name references in custom nodes
            case ObjectClass.custom: {
                const nodeFunction = nodeConfig.function;

                if(nodeFunction) {
                    const functionArguments = nodeFunction.arguments;
                    const functionOutputs = nodeFunction.outputs;
                    if (functionArguments) {
                        functionArguments.forEach(argument =>{
                            if (argument.varName === scopedOldName) {
                                if (parentModuleId !== null && argument.id === varId) argument.varName = scopedNewName;
                                if (parentModuleId === null) argument.varName = scopedNewName;
                                
                            }
                        });
                        node.props.config.function.arguments = functionArguments;
                        this.updateParentNode(node, nodeId, "function", functionArguments, nodeType);
                    }
                    
                    if (functionOutputs) {
                        functionOutputs.forEach(output =>{
                            if (output.varName && output.varName === scopedOldName) {
                                if (parentModuleId !== null && output.id === varId) output.varName = scopedNewName;
                                if (parentModuleId === null) output.varName = scopedNewName;
                                
                            }
                        });
                        node.props.config.function.outputs = functionOutputs;
                        this.updateParentNode(node, nodeId, "function", functionOutputs, nodeType);
                    }
                }
                if(nodeFunction.script) {
                    node.props.config.function.script = nodeFunction.script.replace(oldNamePatternScript, newScriptName);
                    this.updateParentNode(node, nodeId, "function", node.props.config.function, nodeType);
                }
                break;
            }
            // Update variable name references in data nodes
            case ObjectClass.data: {
                const objectHeaders = nodeConfig.headers;
                const nodeScript = nodeConfig.customScript;

                if (objectHeaders) {
                    objectHeaders.forEach(header => {
                        if (header.varName && header.varName === scopedOldName) {
                            if (parentModuleId !== null && header.id === varId) header.varName = scopedNewName;
                            if (parentModuleId === null) header.varName = scopedNewName;
                        }
                        if (header.argumentName && header.argumentName === scopedOldName) {
                            if (parentModuleId !== null && header.id === varId) header.argumentName = scopedNewName;
                            if (parentModuleId === null) header.argumentName = scopedNewName;
                        }
                    });
                    node.props.config.headers = objectHeaders;
                    this.updateParentNode(node, nodeId, "headers", objectHeaders, nodeType);
                }
                if(nodeScript) {
                    node.props.config.customScript = nodeConfig.customScript.replace(oldNamePatternScript, newScriptName);
                    this.updateParentNode(node, nodeId, "customScript", node.props.config.customScript, nodeType);
                }
                break;
            }

            // Update variable name references in data nodes
            case ObjectClass.dataAccessCompletion: {
                const nodeScript = nodeConfig.customScript;

                if(nodeScript) {
                    node.props.config.customScript = nodeConfig.customScript.replace(oldNamePatternScript, newScriptName);
                    this.updateParentNode(node, nodeId, "customScript", node.props.config.customScript, nodeType);
                }
                break;
            }
            // Update variable name references in exit nodes
            case ObjectClass.exit: {
                const objectHeaders = nodeConfig.headers;

                if (objectHeaders) {
                    objectHeaders.forEach(header => {
                        if (header.varName && header.varName === scopedOldName) {
                            if (parentModuleId !== null && header.id === varId) header.varName = scopedNewName;
                            if (parentModuleId === null) header.varName = scopedNewName;
                        }
                        if (header.argumentName && header.argumentName === scopedOldName) {
                            if (parentModuleId !== null && header.id === varId) header.argumentName = scopedNewName;
                            if (parentModuleId === null) header.argumentName = scopedNewName;
                        }
                    });
                    node.props.config.headers = objectHeaders;
                    this.updateParentNode(node, nodeId, "headers", node.props.config.headers, nodeType);
                }
                if(nodeConfig.type === "VARIABLE"){
                    if(nodeConfig.varName === scopedOldName){
                        node.props.config.varName = scopedNewName;
                        this.updateParentNode(node, nodeId, "varName", node.props.config.varName, nodeType);
                    }
                }
                break;
            }
            case ObjectClass.moduleReference: {
                if(parentModuleId === null) {
                    if(nodeConfig.moduleReference && nodeConfig.moduleReference === scopedOldName) {
                        node.props.config.moduleReference = scopedNewName;
                        this.updateParentNode(node, node.id, "moduleReference", nodeConfig.moduleReference, nodeType);
                    }
                }
                break;
            }
            //Update variable name reference in all prompt text
            case ObjectClass.prompt: {
                const oldRefrence = `{{${scopedOldName}}}`;
                const newRefrence = `{{${scopedNewName}}}`;
                const updateChannelContent = (channelContent) => {
                    Object.keys(channelContent ?? {}).forEach((languageCode) => {
                        if (channelContent[languageCode].includes(oldRefrence)) {
                            channelContent[languageCode] = channelContent[languageCode].replaceAll(oldRefrence, newRefrence);
                            this.updateParentNode(node, nodeId, "text", channelContent[languageCode], nodeType);
                        }
                    });
                };

                const { channel, text } = nodeConfig;
                updateChannelContent(text);
                Object.keys(channel ?? {}).forEach((channelType) => {
                    updateChannelContent(channel[channelType]);
                });
                break;
            }
            case ObjectClass.promptReference: {
                if(parentModuleId === null) {
                    if(nodeConfig.promptReference && nodeConfig.promptReference === scopedOldName) {
                        node.props.config.promptReference = scopedNewName;
                        this.updateParentNode(node, node.id, "promptReference", nodeConfig.promptReference, nodeType);
                    }
                }
                break;
            }
            // Update variable name references in report nodes
            case ObjectClass.report: {
                Object.keys(nodeConfig).forEach(key => {
                    switch (key) {
                        case "transferReason":
                        case "transferDestination":
                        case "scriptProgress":
                            if(nodeConfig[key] === scopedOldName) {
                                nodeConfig[key] = scopedNewName;
                                node.props.config[key] = nodeConfig[key];
                                this.updateParentNode(node, nodeId, key, nodeConfig[key], nodeType);
                            }
                        case "billing":
                        case "customTag":
                            if(nodeConfig[key].value && nodeConfig[key].value === scopedOldName) {
                                nodeConfig[key].value = scopedNewName;
                                node.props.config[key] = nodeConfig[key];
                                this.updateParentNode(node, nodeId, key, nodeConfig[key], nodeType);
                            }
                    }
                });
                break;
            }
            // Update variable name references in tabular router nodes
            case ObjectClass.tab: {
                const matchOrder = nodeConfig.matchOrder;
                const outputTo = nodeConfig.outputTo;

                if (matchOrder) {
                    matchOrder.forEach(mo => {
                        if (mo.varName && mo.varName === scopedOldName) {
                            if (parentModuleId !== null && mo.id === varId) mo.varName = scopedNewName;
                            if (parentModuleId === null) mo.varName = scopedNewName;
                            
                        }
                    });
                    node.props.config.matchOrder = matchOrder;
                    this.updateParentNode(node, nodeId, "matchOrder", node.props.config.matchOrder, nodeType);
                }

                if (outputTo) {
                    outputTo.forEach(mo => {
                        if (mo.varName && mo.varName === scopedOldName) {
                            if (parentModuleId !== null && mo.id === varId) mo.varName = scopedNewName;
                            if (parentModuleId === null) mo.varName = scopedNewName;
                            
                        }
                    });
                    node.props.config.outputTo = outputTo;
                    this.updateParentNode(node, nodeId, "outputTo", node.props.config.outputTo, nodeType);
                }
                break;
            }
        }
    };

    /**
     * 
     * @param {*} varName 
     * @param {*} currentModuleId 
    */
    refactorVariableNameinGlobalIntents(oldVarName,newVarName, moduleId) {
        var moduleUsedInglobalIntents = window.apm.serialized.globalIntents.filter(gi => gi.module === moduleId);
        moduleUsedInglobalIntents.forEach(gi => {
            var varIndex = gi.moduleProperties.props.config.inputs.findIndex(input => input.argumentName === oldVarName);
            if (varIndex !== -1) {
                gi.moduleProperties.props.config.inputs[varIndex].argumentName = newVarName;
                gi.moduleProperties.props.lastLocalChange = window.apm.timestamp;
            }
        })
    }
    /**
     * Set or create a variable
     * @param {string} varName
     * @param {string} varType
     * @param {string|null} usedModuleId
     * @param {string|null} parentModuleId If local variable, use module id. If global variable, use null
     * @param {object|null} config
     */
    setVariable(varName, varType, usedModuleId = null, parentModuleId = null, config = null) {
        const existingVar = this.getVariable(varName, parentModuleId);
        const currentDiagramId = window.apm.getCurrentDiagramWidget().props.node.id;
        if (!existingVar) {
            const variableDataModel = new VariableDataModel();
            variableDataModel.setProperty("parentModule", parentModuleId);
            
            if (usedModuleId) {
                variableDataModel.setProperty("usedIn", [usedModuleId]);
            }
            else {
                variableDataModel.setProperty("usedIn", []);
            }
            if (config) {
                variableDataModel.props.config = JSON.parse(JSON.stringify(config));
            }
            
            variableDataModel.name = varName;
            variableDataModel.props.config.varName = varName;
            variableDataModel.props.config.type = varType;
            // TODO: we should remove/test props.config.name from variables
            variableDataModel.props.config.name = variableDataModel.getConfig("name");

            var refVars;
            if (parentModuleId) {
                variableDataModel.props.parentModule = parentModuleId;
                const parentModule = this.getNode(parentModuleId);

                if (parentModule.localVariables === undefined) {
                    parentModule.localVariables = [];
                }
                refVars = parentModule.localVariables;
            } else {
                refVars = this.serialized.components.variables;
            }
            if (!variableDataModel.props.createdTime) variableDataModel.props.createdTime = window.apm.timestamp;
            variableDataModel.props.lastLocalChange = window.apm.timestamp;
            refVars.push(variableDataModel.serialize());

            if (parentModuleId && parentModuleId === currentDiagramId) {
                $("#tm_container").jstree().create_node("variables", Utility.getTreeNode(variableDataModel), "last");
            } else {
                $("#tl_container").jstree().create_node("variables", Utility.getTreeNode(variableDataModel), "last");
            }
        }
        this.setSerializedChanged();
    }

    /**
     * Remove a variable
     * @param varIdOrName {string}
     * @param usedModuleId {string} If remove used reference, use module id. If remove entire variable, use null
     * @param parentModuleId {string|null} If local variable, use module id. If global variable, use null
     */
    removeVariable(varIdOrName, usedModuleId = null, parentModuleId = null) {
        var refVars = [];
        if (parentModuleId) {
            const parentModule = this.getNode(parentModuleId);
            if (parentModule !== null) {
                if (parentModule.localVariables === undefined) {
                    parentModule.localVariables = [];
                }
                refVars = parentModule.localVariables;
            }
        } else {
            refVars = this.serialized.components.variables;
        }
        for (var i = 0; i < refVars.length; i++) {
            if (refVars[i].id === varIdOrName || refVars[i].props.config.varName === varIdOrName) {
                if (usedModuleId) {
                    const varIndex = refVars[i].props.usedIn.findIndex(element => element === usedModuleId);
                    if (varIndex !== -1) {
                        refVars[i].props.usedIn.splice(varIndex, 1);
                        refVars[i].props.lastLocalChange = window.apm.timestamp;
                    }
                } else {
                    refVars.splice(i, 1);
                    i--;
                }
            }
        }
        this.setSerializedChanged();
    }

    reindexVariables(moduleId) {
        // clear used index from all global variables
        for (var i = 0; i < this.serialized.components.variables.length; i++) {
            const variable = this.serialized.components.variables[i];
            const usedIndex = variable.props.usedIn.indexOf(moduleId);
            if (usedIndex !== -1) {
                variable.props.usedIn.splice(usedIndex, 1);
            }
        }
        const module = this.getNode(moduleId);
        // clear used index from all local variables
        for (var i = 0; i < module.localVariables.length; i++) {
            const variable = module.localVariables[i];
            const usedIndex = variable.props.usedIn.indexOf(moduleId);
            if (usedIndex !== -1) {
                variable.props.usedIn.splice(usedIndex, 1);
            }
        }
        // add all used variables back
        for (const ver in module.versions) {
            if (module.versions.hasOwnProperty(ver)) {
                const flow = module.versions[ver].flow;
                for (const fKey in flow) {
                    if (flow.hasOwnProperty(fKey) && (fKey === "links" || fKey === "nodes")) {
                        for (var i = 0; i < flow[fKey].length; i++) {
                            const ln = flow[fKey][i];
                            if (ln.props && ln.props.config) {
                                for (const cKey in ln.props.config) {
                                    if (ln.props.config.hasOwnProperty(cKey)) {
                                        // find variable references
                                        if (cKey === "inputs" || cKey === "outputs") {
                                            for (var j = 0; j < ln.props.config[cKey].length; j++) {
                                                const io = ln.props.config[cKey][j];
                                                if (io.varName && io.varName.indexOf(".") !== -1) {
                                                    const scope = io.varName.split(".")[0];
                                                    const varName = io.varName.split(".")[1];
                                                    if (scope === Constant.k.SCOPE_APPLICATION) {
                                                        this.setVariable(varName, null, moduleId, null);
                                                    }
                                                    if (scope === Constant.k.SCOPE_MODULE) {
                                                        this.setVariable(varName, null, moduleId, moduleId);
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    setIntentRef(intentId, targetIntentId, type) {
        const target = this.getNode(targetIntentId);
        if (!target) {
            return;
        }
        if (type === "intents") { // add to target's categories
            if (!target.props.config.categories) {
                target.props.config.categories = [];
            }
            target.props.config.categories.push(intentId);
        }
        if (type === "categories") { // add to target's intents
            if (!target.props.config.intents) {
                target.props.config.intents = [];
            }
            target.props.config.intents.push(intentId);
            target.props.iconClass = IconClass.tag;
            target.props.nodeTypeName = NodeTypeName.intentCategory;
            const treeL = $("#tl_container");
            const n = treeL.jstree().get_node(targetIntentId);
            treeL.jstree().set_icon(n, target.props.iconClass);
        }
        this.setSerializedChanged();
    }

    removeIntentRef(intentId, targetIntentId, type) {
        const target = this.getNode(targetIntentId);
        if (!target) {
            return;
        }
        if (type === "intents") { // remove from target's categories
            if (!target.props.config.categories) {
                target.props.config.categories = [];
                return;
            }
            const inIndex = target.props.config.categories.findIndex(element => element === intentId);
            if (inIndex !== -1) {
                target.props.config.categories.splice(inIndex, 1);
            } else {
                return;
            }
        }
        if (type === "categories") { // remove from target's intents
            if (!target.props.config.intents) {
                target.props.config.intents = [];
            }
            const inIndex = target.props.config.intents.findIndex(element => element === intentId);
            if (inIndex !== -1) {
                target.props.config.intents.splice(inIndex, 1);
            } else {
                return;
            }
            const treeL = $("#tl_container");
            if (target.props.config.intents.length) {
                target.props.iconClass = IconClass.tag;
                target.props.nodeTypeName = NodeTypeName.intentCategory;
            } else {
                target.props.iconClass = IconClass.radio;
                target.props.nodeTypeName = NodeTypeName.intent;
            }
            const n = treeL.jstree().get_node(targetIntentId);
            treeL.jstree().set_icon(n, target.props.iconClass);
        }
        this.setSerializedChanged();
    }

    /**
     * Init tree data
     */
    getTree() {
        const tree = [];
        const components = JSON.parse(JSON.stringify(this.serialized.components));

        for (const type in components) {
            if (components.hasOwnProperty(type)) {
                const children = [];
                if (type === "objects") {
                    const objTypes = {};
                    for (var i = 0; i < components[type].length; i++) {
                        var node = components[type][i];
                        if (node.isHidden) {
                            continue;

                        }
                        objTypes[node.props.nodeTypeName] ??= [];
                        const leaf = Utility.getTreeNode(node);
                        objTypes[node.props.nodeTypeName].push(leaf);
                    }
                    for (const objType in objTypes) {
                        if (objTypes.hasOwnProperty(objType)) {
                            children.push({
                                id: objType,
                                text: objType,
                                type: type,
                                children: objTypes[objType]
                            });
                        }
                    }
                } else if (type === "modules") {
                    const thefolders = {};

                    if (this.serialized.folders) {
                        let folders = JSON.parse(JSON.stringify(this.serialized.folders));
                        for (const folder in folders.modules) {
                            let foldername = folders.modules[folder].name;
                            thefolders[foldername] = [];
                        }
                    }

                    for (var d = 0; d < components[type].length; d++) {
                        var node = components[type][d];
                        if (node.id === rootModuleId) {
                            continue;
                        }
                        if (node.isHidden) {
                            continue;
                        }

                        let match = false;

                        if (this.serialized.folders) {
                            let folders = JSON.parse(JSON.stringify(this.serialized.folders));
                            for (const folder in folders.modules) {
                                if (folders.modules[folder].children) {
                                    let foldername = folders.modules[folder].name;

                                    let folderchildren = folders.modules[folder].children;
                                    for (var p = 0; p < folderchildren.length; p++) {
                                        if (thefolders?.[foldername] && folderchildren?.[p] === node.id) {
                                            var leaf = Utility.getTreeNode(node);
                                            thefolders[foldername].push(leaf);
                                            match = true;
                                        }
                                    }
                                }
                            }
                        }

                        if (match === false) {
                            var leaf = Utility.getTreeNode(node);
                            children.push(leaf);
                        }
                    }
                    if (this.serialized.folders) {
                        for (const afolder in thefolders) {

                            children.push({
                                id: afolder,
                                text: afolder,
                                icon: IconClass.folder,
                                type: type,
                                state: { opened: false },
                                children: thefolders[afolder]
                            });
                        }
                    }
                } else if (
                    type === "variables"
                    && components[type].filter(v => v.props.config.scope).length > 0
                    && this.serialized.omniChannel.isOmniChannel
                ) {
                    const varTypesMap = {};

                    components[type].forEach(variable => {
                        // scope can be "session" or "conversation" 
                        if (!varTypesMap.hasOwnProperty(variable.props.config.scope)) {
                            varTypesMap[variable.props.config.scope] = [];
                        }

                        const leaf = Utility.getTreeNode(variable);
                        // set up the typesMap
                        varTypesMap[variable.props.config.scope].push(leaf);

                    });

                    // UI: use typeMaps to organzie how variables are displayed in jsTree
                    // "session" scoped are imediate children of Variables 
                    if (varTypesMap["session"].length > 0) {
                        varTypesMap["session"].forEach(v => {
                            children.push(v);
                        })
                    }

                    // "conversation" scoped are imediate children of CONVERSATION folder 
                    if (varTypesMap["conversation"] && varTypesMap["conversation"].length > 0) {
                        children.push({
                            id: "CONVERSATION",
                            text: "CONVERSATION",
                            icon: IconClass.folder,
                            type: type,
                            state: { opened: false },
                            children: varTypesMap["conversation"]
                        });
                    }

                } else {
                    for (var d = 0; d < components[type].length; d++) {
                        var node = components[type][d];
                        // prevent root module displayed in the tree
                        if (type === "intents") {
                            if (node.isHidden) {
                                continue;
                            }
                        }
                        var leaf = Utility.getTreeNode(node);
                        children.push(leaf);
                    }
                }

                tree.push({
                    id: type,
                    text: window.l.library[type],
                    type: type,
                    children: children
                });
            }
        }
        return tree;
    }

    /**
     * Refresh jstree
     * @param {boolean|undefined} deselectAll Flag to deselect all current selected nodes on the tree.
     */
    refreshApplicationTree(deselectAll = false) {
        updateTree(deselectAll);
    }

    /**
     * Is the variable name a duplicated one?
     * @param {string} varName
     * @param {string} parentModuleId
     * @param {string} id ID of variable node itself so that it can be excluded from duplicate check
     * @returns {boolean}
     */
    isVariableDuplicated(varName, parentModuleId = null, id = null) {
        var refVars;
        if (parentModuleId) {
            const parentModule = this.getNode(parentModuleId);
            if (parentModule.localVariables === undefined) {
                parentModule.localVariables = [];
            }
            refVars = parentModule.localVariables;
        } else {
            refVars = this.serialized.components.variables;
        }
        for (var i = 0; i < refVars.length; i++) {
            if (refVars[i].id !== id && refVars[i].props.config.varName === varName) {
                return true;
            }
        }
        return false;
    }
    isfunctionNameDuplicated(functionName, isNew){
        let functionNames = [];
        this.serialized.functions.forEach(rf => {
          functionNames.push(rf.name);
        });
        if(functionNames.filter(m=>m===functionName).length>0&&isNew){
            return true;
        }
        else if(functionNames.filter(m=>m===functionName).length>1&&!isNew){
            return true        
        }
        else {
            return false;
        }
        
    }

    /**
     * Is the folder name a duplicated one?
     * @param {string} folderName
     * @param {string} id ID of folder node itself so that it can be excluded from duplicate check
     * @returns {boolean}
     */
    isFolderDuplicated(folderName, id = null) {

        let folders = this.serialized.folders.modules || [];

        for (var i = 0; i < folders.length; i++) {

            if (folders[i].id !== id && folders[i].name === folderName.toUpperCase().replace(" ", "_")) {
                return true;
            }
        }
        return false;
    }

    setCanRemove(usedIn) {
        var usedInKeys = Object.keys(usedIn);
        let canRemove = true;
        usedInKeys.forEach(key => {
            if (usedIn[key].length > 0) {
                w2popup.close();
                canRemove = false;
            }
        })
        return canRemove;
    }

    removeComponents(callback = null, node = null, treeNode = null) {
        let canRemove = true, errorMessage = window.l.notification.cannot_delete;
        if (!node && !treeNode) {
            node = this.removedNode;
            treeNode = this.treeNode;
            let usedIn = [];
            switch (node.type) {
                case "module":
                    usedIn = ModuleUsedIn.getModuleUsedIn(node, this.serialized, node.props.config);
                    canRemove = this.setCanRemove(usedIn);
                    if(this.serialized.globalIntents){
                        for (var modglobint of this.serialized.globalIntents) {
                            if (node.id === modglobint.module) {
                                canRemove = false;
                                errorMessage = "Remove Module From Global Intent Map First";
                            }
                        }
                    }
                    break;
                case "intent":
                    usedIn = IntentUsedIn.getIntentUsedIn(node, this.serialized);
                    renderByRoute(RootRoute.settings);

                    const autoRemoveableUsages = ['intentsUsedInDtmfNodes', 'intentsUsedInInputNodes'];
                    canRemove = this.setCanRemove(omit(usedIn, autoRemoveableUsages));

                    // Auto remove usage if other usage is not present.
                    if (canRemove) {
                        usedIn['intentsUsedInInputNodes'].forEach((input) => {
                            const inputObjectConfig = input.node.props.config;
                            if (inputObjectConfig.impliedIntent === node.id) {
                                inputObjectConfig.impliedIntent="";
                                window.apm.updateParentNode(input.node, input.node.id, 'impliedIntent', "", "object")
                            }
                            if (inputObjectConfig.expectedIntent === node.id) {
                                inputObjectConfig.expectedIntent="";
                                window.apm.updateParentNode(input.node,input.node.id,'expectedIntent',"","object")
                            }
                            // When an intent is deleted from the application, remove the intent id if it is assigned to as a dtmfIntent
                            if (inputObjectConfig.dtmfIntent === node.id) {
                                inputObjectConfig.dtmfIntent = "";
                                window.apm.updateParentNode(input.node,input.node.id,'dtmfIntent', "", "object")
                            }
                            if (inputObjectConfig.localIntents.length) {
                                inputObjectConfig.localIntents.forEach((li,l) => {
                                    if (li === node.id) {
                                        inputObjectConfig.localIntents.splice(l, 1);
                                    }
                                })
                                window.apm.updateParentNode(input.node,input.node.id,'localIntents',inputObjectConfig.localIntents,"object")
                            }
                        });

                        usedIn['intentsUsedInDtmfNodes'].forEach((dtmf) => {
                            const dtmfObjectConfig = dtmf.node.props.config;
                            dtmfObjectConfig.selections.forEach((selection, s) => {
                                if (selection.value === node.id) {
                                    dtmfObjectConfig.selections[s].value = "";
                                }
                                window.apm.updateParentNode(dtmf.node,dtmf.node.id,'selections',dtmfObjectConfig.selections,"object");
                            });
                        });
                    }

                    if (!canRemove && usedIn['intentsUsedInGlobalIntents'].length > 0) {
                        errorMessage = "Remove Intent From Global Intent Map First";
                    }
                    break;
                case "object":
                    usedIn = ObjectsUsedIn.getObjectUsedIn(node.id, this.serialized, node.props.config);
                    canRemove = this.setCanRemove(usedIn);
                    break;
                case "variable":
                    usedIn = VariablesUsedIn.getVariableUsedIn(node, this.serialized);  
                    if (usedIn.length > 0) {
                        w2popup.close();
                        canRemove = false;
                    }
                    break;
            }
        }
       
        if (canRemove) { // TODO: ,_ remove condition, we checked this above after switch statement
            $('#tl_container').jstree().delete_node(node);
            if (node.type === "object") {
                // remove empty type name folder
                if ($('#tl_container').jstree().get_node(treeNode.parent).children.length === 0) {
                    $('#tl_container').jstree().delete_node(treeNode.parent);
                }
            }
            this.removeNode(node.id, canRemove);
            if (node.props.isRootModule && node.props.isCommonModule) {
                for (let i = 0; i < this.serialized.appCommonModules.length; i++) {
                    if (node.name === this.serialized.appCommonModules[i].application) {
                        this.serialized.appCommonModules.splice(this.serialized.appCommonModules[i], 1);
                    }
                }
            }
            useCanvasTabs.getState().closeTab(node.id);
            document.dispatchEvent(new CustomEvent("ev-dialog-variable-changed"));
        } else {
            this.notification.post("error", errorMessage);
        }
        w2popup.close();
        if (callback) {
            callback();
        }
    }

    removeCommonModule(node){
        const usedIn = ModuleUsedIn.getModuleUsedIn(node, this.serialized, node.props.config);
        if(usedIn['usedInGlobalIntents'].length>0){
            this.notification.post("error","Remove Module From Global Intent Map First");
        }
        else{
            this.commonModuleManager.removeCommonModule(node.id);
        }
       
    }

    //better remove this if we don't use it do delete common modules
    removeCommonModules() {
        const node = this.removedNode;
        const treeNode = this.treeNode;
        let canRemove = true;

        if (node.type === "variable") {
            const varModel = this.getVariable(node.id);
            if (varModel && varModel.props.usedIn && varModel.props.usedIn.length > 0) {
                canRemove = false;
                errorMessage = window.l.context_menu.cannot_delete_variable;
            }
        }
        if (canRemove) {
            if (window.apm.serialized.graphQLSchemas) {
                window.apm.serialized.graphQLSchemas.forEach(ele => {
                    if (ele.isCommonModule && ele.moduleId === node.props.moduleId) {
                        var ghIndex = window.apm.serialized.graphQLSchemas.findIndex(gh => gh.id === gh.id && gh.moduleId === node.props.moduleId);
                        if (ghIndex !== -1) window.apm.serialized.graphQLSchemas.splice(ghIndex, 1);
                    }
                })
            }
            if (window.apm.serialized.csvData) {
                window.apm.serialized.csvData.forEach(ele => {
                    if (ele.isCommonModule && ele.moduleId === node.props.moduleId) {
                        var ghIndex = window.apm.serialized.csvData.findIndex(gh => gh.id === ele.id && gh.moduleId === node.props.moduleId);
                        if (ghIndex !== -1) window.apm.serialized.csvData.splice(ghIndex, 1);
                    }
                })
            }
            $('#tl_container').jstree().delete_node(node);
            if (node.type === "object") {
                // remove empty type name folder
                if ($('#tl_container').jstree().get_node(treeNode.parent).children.length === 0) {
                    $('#tl_container').jstree().delete_node(treeNode.parent);
                }
            }
            if (node.props.isRootModule && node.props.isCommonModule) {
                for (let i = 0; i < this.serialized.appCommonModules.length; i++) {
                    if (!Utility.getIsCommonModule(this.serialized)) {
                        if (node.props.moduleId === this.serialized.appCommonModules[i]._id) {
                            this.serialized.appCommonModules.splice(this.serialized.appCommonModules[i], 1);
                        }
                    }
                    else {
                        if (node.props.config.subModuleId === this.serialized.appCommonModules[i]._id) {
                            this.serialized.appCommonModules.splice(this.serialized.appCommonModules[i], 1);
                        }
                    }
                }
            }
            var commonModules = [];
            for (var g = 0; g < this.serialized.components.modules.length; g++) {
                if (!Utility.getIsCommonModule(this.serialized)) {
                    if (this.serialized.components.modules[g].props.isCommonModule 
                            && this.serialized.components.modules[g].props.moduleId === node.props.moduleId 
                            && this.serialized.components.modules[g].props.moduleApp === node.props.moduleApp) {
                        commonModules.push(this.serialized.components.modules[g]);
                    }
                }
                else {
                    if (node.props.config.subModuleId !== node.props.moduleId) {
                        if (this.serialized.components.modules[g].props.isCommonModule 
                            && this.serialized.components.modules[g].props.config.subModuleId === node.props.config.subModuleId 
                            && this.serialized.components.modules[g].props.config.subModuleApp === node.props.config.subModuleApp) {
                            commonModules.push(this.serialized.components.modules[g]);
                        }
                    }
                    else {
                        if (this.serialized.components.modules[g].props.isCommonModule 
                            && this.serialized.components.modules[g].props.moduleId === node.props.moduleId 
                            && this.serialized.components.modules[g].props.moduleApp === node.props.moduleApp) {
                            commonModules.push(this.serialized.components.modules[g]);
                        }
                    }
                }
            }
            const { closeTab } = useCanvasTabs.getState();
            for (var b = 0; b < commonModules.length; b++) {
                this.removeNode(commonModules[b].id);
                closeTab(commonModules[b].id);
            }
        }

        // update settings props
        renderByRoute(RootRoute.settings);
        //update intents props
        renderByRoute(RootRoute.intents);

        w2popup.close();
    }

    /**
     * Is the node name a duplicated one within the given scope defined by treeview?
     * @param {jQuery} treeDom Treeview reference for duplicate check context
     * @param {string} name
     * @param {string} type
     * @param {NodeTypeName|null} typeName required for objects
     * @param {string|null} id ID of node itself so that it can be excluded from duplicate check
     * @returns {boolean}
     */
    isTreeNameDuplicated(treeDom, name, type, typeName = null, id = null) {
        if (!treeDom) {
            return false;
        }

        if (typeName === NodeTypeName.exit) return false;

        var data = treeDom.jstree().get_json('#');
        const typeIndex = data.findIndex(element => element.type === type + "s");
        if (data[typeIndex]) {
            for (var j = 0; j < data[typeIndex].children.length; j++) {
                const child = data[typeIndex].children[j];
                if (child.id === typeName) {
                    for (var k = 0; k < child.children.length; k++) {
                        if (!id && child.children[k].text === name) {
                            return true;
                        }
                        if (id && child.children[k].id !== id && child.children[k].text === name) {
                            return true;
                        }
                    }
                } else {
                    if (!id && child.text === name) {
                        return true;
                    }
                    if (id && child.id !== id && child.text === name) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    /**
     * Get a valid node name based on given scope defined by treeview, such that name has no duplicate
     * @param {jQuery} treeDom Treeview reference for duplicate check context
     * @param {string} name
     * @param {string} type
     * @param {NodeTypeName|null} typeName
     * @param {string|null} id ID of node itself for duplicate check
     * @returns {string}
     */
    getEligibleTreeName(treeDom, name, type, typeName = null, id = null) {
        let newName = name;
        while (this.isTreeNameDuplicated(treeDom, newName, type, typeName, id)) {
            const fields = newName.split('_');
            if (/^\d+$/.test(fields[fields.length - 1])) {
                fields[fields.length - 1]++;
            } else {
                fields.push("2");
            }
            newName = fields.join('_');
        }
        return newName;
    }
    isNodeNameDuplicated(treeDom, name, type, typeName = null, id = null) {
        if (!treeDom) {
            return false;
        }//Allowed duplicates for Prompt and Module
        var data = treeDom.jstree().get_json('#');
        const typeIndex = data.findIndex(element => element.type === type + "s");
        if (data[typeIndex]) {
            for (var j = 0; j < data[typeIndex].children.length; j++) {
                const child = data[typeIndex].children[j];
                if (child.id === typeName) {
                    for (var k = 0; k < child.children.length; k++) {
                        if (!id && child.children[k].text === name) {
                            return true;
                        }
                        if (id && child.children[k].id !== id && child.children[k].text === name) {
                            return true;
                        }
                    }
                } else {
                    if (!id && child.text === name) {
                        return true;
                    }
                    if (id && child.id !== id && child.text === name) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    getEligibleNodeName(treeDom, name, type, typeName = null, id = null) {
        var newName = name;
        while (this.isNodeNameDuplicated(treeDom, newName, type, typeName, id)) {
            var fields = newName.split(' ');
            if (/\scopy\s\d+$/.test(newName)) {
                fields[fields.length - 1]++;
            } else if (/\scopy$/.test(newName)) {
                fields.push("2");
            } else {
                fields.push("_CLONE");
            }
            newName = fields.join('');
        }
        return newName;
    }
    
    /**
     * Get a valid variable name based on given scope defined by treeview, such that name has no duplicate
     * @param {string} name
     * @param {string|null} parentModuleId
     * @returns {string}
     */
    getEligibleVariableName(name, parentModuleId = null) {
        var newName = name;
        while (this.isVariableDuplicated(newName, parentModuleId)) {
            var fields = newName.split('_');
            if (/copy_\d+$/.test(newName)) {
                fields[fields.length - 1]++;
            } else if (/copy$/.test(newName)) {
                fields.push("2");
            } else {
                fields.push("copy");
            }
            newName = fields.join('_');
        }
        return newName;
    }
    /**
     * Canvas widget for the active tab, if it is already in the cache.
     *
     * @deprecated Use @see waitForCanvas instead.
     *
     * May be missing while a newly opened tab is still mounting. Callers that
     * just `addTab`'d should @see waitForCanvas instead of assuming this is
     * defined.
     *
     * @returns {CustomDiagramWidget} instance of current diagram.
     */
    getCurrentDiagramWidget() {
        const { activeTabId } = useCanvasTabs.getState();
        return this.cache.get(Constant.k.NAMESPACE_DIAGRAM_WIDGET, activeTabId);
    }

    /**
     * Post custom event to the canvas instance in focus
     * @param {Object} event Custom event
     */
    postEventToCurrentView(event) {
        this.getCurrentDiagramWidget()?.handleCustomEvent(event);
    }

    postEventToAllViews(event) {
        /** @type {Record<string, CustomDiagramWidget>} */
        const allWidgets = this.cache.getAll(Constant.k.NAMESPACE_DIAGRAM_WIDGET);
        for (const widget of Object.values(allWidgets)) {
            widget.handleCustomEvent(event);
        }
    }

    /**
     * Print debug messages
     * @param {string} title
     * @param {*} content
     * @param {string} type
     */
    printDebug(title, content, type) {
        if (this.config.debug.enabled && this.config.debug[type]) {
            console.log("::" + type + "::" + title, content);
        }
    }
    openValidationWindow() {
        w2popup.close();
        window.apm.postEventToCurrentView({ target: "request-application-validation" });
    }
    
    /**
     * Opens popup with CommonModuleChangeVersionDialog
     * Allows users to change the version of Common Modules installed in their applicatyions
     * @param {string} selectedVersion 
     * @param {object} context 
     */
    openCommonModuleChangeVersionWindow(selectedVersion, context) {
        window.apm.popup("Changing Common Module Version",
            '<div class="change_version_container" style="width: 100%"></div>',
            '<button class="w2ui-btn primary add-button">Continue</button> ' +
            '<button class="w2ui-btn secondary">' + window.l.popup.close + '</button>',
            [
                ".change_version_container",
                () => {
                    ReactDOM.render(
                        <CommonModuleChangeVersionDialog
                            node={context}
                            name={context.props.moduleApp}
                            currentVersion={context.props.config.version}
                            selectedVersion={selectedVersion} />, $(".change_version_container").get(0));

                    $(".w2ui-popup").find(".w2ui-btn.primary").on("click", () => {
                        document.dispatchEvent(new CustomEvent('ev-dialog-download-common-module'));
                    });

                    $(".w2ui-popup").find(".w2ui-btn.secondary").on("click", () => {
                        window.apm.postEventToCurrentView({target: "request-hide-property-panel"});
                        document.dispatchEvent(new CustomEvent('ev-dialog-close'));
                        w2popup.close();
                    });

                }
            ],
            425, // height
            null, // node
            null, // treenode
            false, // showClose
            520
        );
    }

    openDisabledItemsWindow() {
        this.popup("Disabled items",
            '<div class="disabled_items_container" style="height: 98%"></div>',
            '<button class="w2ui-btn primary">' + window.l.popup.close + '</button> ',
            [
                ".disabled_items_container",

                () => {
                    const $el = $(".disabled_items_container");
                    this.disabledItemsDialog = this.reactDOMComponents.renderDialog({
                        name: 'disabledItemsDialog',
                        props: {
                            modules: window.apm.serialized.components.modules,
                            message: "Searching for disabled items..."
                        }
                        ,
                        $el
                    });

                    $(".w2ui-popup").find(".w2ui-btn.primary").on("click", () => {
                        w2popup.close();
                        this.reactDOMComponents.unmountComponentAtNode($el);
                    });
                }
            ]
            ,
            700, // height
            null, // node
            null, // treenode
            false // showClose
        );
    }

    /** @param {Design} mainDesign - Can be latest or current (non-latest) version */
    /** @param {boolean} isHotfix - Whether merging to a non-latest version */
    openMergeBranchWindow(mainDesign, isHotfix = false) {
        const closePopup = ($el) => {
            this.reactDOMComponents.unmountComponentAtNode($el);
            w2popup.close();
        }

        const mergePreviewButtonId = "merge_preview_button";

        window.apm.popup('Merge Branch',
            '<div class="branch_and_merge_container"></div>',
            [
                '<button id="' + mergePreviewButtonId + '" class="w2ui-btn primary" disabled>' + window.l.popup.merge_preview + '</button>',
                '<button class="w2ui-btn secondary">' + window.l.popup.cancel + '</button>'
            ],
            [
                "branch_and_merge_container",
                () => {
                $(".branch_and_merge_container").ready(() => {
                    const popupBoxHeight = $("div.w2ui-box").prop("offsetHeight");
                    const branchListContainerHeight = popupBoxHeight / 2;
                    const $el = $(".branch_and_merge_container").get(0);

                    const mergeBranchDialog = ReactDOM.render((
                        <MergeBranchDialog 
                            serialized={this.serialized}
                            popup={w2popup}
                            appContext={this}
                            closeAction={closePopup}
                            closeElement={$el}
                            mergePreviewButtonId={mergePreviewButtonId}
                            branchListContainerHeight={branchListContainerHeight}
                            isHotfix={isHotfix}
                        />
                    ), $el);

                    $(".w2ui-popup").find(".w2ui-btn#" + mergePreviewButtonId).on("click", async () => {
                        try {
                            closePopup($el);
                            const { selected } = mergeBranchDialog.state;
                            loadingService.addMessage('Preparing merge for review...', 'merge-preview');
                            
                            const {
                                sourceDesign,
                                sourceOriginDesign,
                                targetDesign,
                                targetOriginDesign
                            } = await avsService.getDocumentsForMerge(
                                selected.branchId,
                                mainDesign
                            );

                            // If merging a hotfix branch, validate that Main is equal or higher than the version it was branched from
                            if (isHotfix) {
                                const initBranchEvent = getLatestBranchingEvent(
                                    sourceDesign.branching,
                                    DesignBranchingEventType.initBranch
                                );
                                const branchedAt = initBranchEvent?.branched_at;
                                const isMainTooOld = (
                                    !branchedAt
                                    || compareDesignVersions(mainDesign.version, branchedAt) === CompareResult.bIsLarger
                                );
                                
                                if (isMainTooOld) {
                                    loadingService.removeMessage('merge-preview');
                                    errorMessageService.addError(new Error(`Cannot merge branch "${selected.application}". This branch was created from Main version ${branchedAt || 'unknown'}, but you are currently on version ${mainDesign.version}. You can only merge into Main version ${branchedAt || 'unknown'} or higher.`));
                                    return;
                                }
                            }

                            loadingService.removeMessage('merge-preview');

                            // UI prevents the user from selecting branches that have already been merged.
                            if (sourceDesign.version === sourceOriginDesign.version) {
                                throw new Error('The latest version of the branch has already been merged!');
                            }
                            
                            await MergeDialog.open({
                                defaultCommitMessage: `Branch merged: ${sourceDesign.branchName} merged at version ${sourceDesign.version}`,
                                isHotfix,
                                sourceDesign,
                                sourceOriginDesign,
                                targetDesign,
                                targetOriginDesign,
                                type: MergeDialogType.merge
                            });
                        } catch (error) {
                            loadingService.removeMessage('merge-preview');
                            errorMessageService.addError(error);
                        }
                    });

                    $(".w2ui-popup").find(".w2ui-btn.secondary").on("click", () => {
                        closePopup($el);
                    });

                });

            }],
            520, // height
            null, // node
            null, // treenode
            false, // showClose
            620 // width
        );
    }

    openCommonModulesWindow(mod) {

        const proxyNode = new NodeRegistry.r[mod._class](mod.name, 0, 0, mod.props, mod.id);
        if (mod._class === DesignClass.module) {
            proxyNode.setProperty("ref", mod.id);
        }
        proxyNode.setProperty("parentComponentID", mod.id);

        this.popup("Set Common Module Defaults",
            '<div class="common_module_defaults_container" style="height: 98%"></div>',
            '<button class="w2ui-btn primary">' + window.l.popup.save + '</button> ',
            () => {
                $(".common_module_defaults_container").ready(() => {
                    const $el = $(".common_module_defaults_container");
                    this.commonModuleDefaultsDialog = this.reactDOMComponents.renderDialog({
                        name: 'setCommonModuleDefaults',
                        props: {
                            module: proxyNode
                        }
                        ,
                        $el
                    });

                    $(".w2ui-popup").find(".w2ui-btn.primary").on("click", () => {
                        //this.reactDOMComponents.unmountComponentAtNode($el);
                        w2popup.close();
                        
                    });

                });

            },
            600, // height
            null, // node
            null, // treenode
            false, // showClose,
            400
        );
    }

    /**
     * Show popup dialog
     * @param {string} title
     * @param {string} htmlBody HTML markup for dialog content
     * @param {string} buttons HTML markup for buttons
     * @param {Function} openCallback Callback reference for after the dialog is opened
     * @param {number} height Dialog container height
     */
    popup(title = "<div class='empty'></div>", htmlBody, buttons, openCallback = null, height = 700, node = null, treeNode = null, showClose = true, width = 750) {
        // disable auto focus on popup buttons
        w2popup.focus = function () { };
        w2popup.open({
            title: title,
            body: htmlBody,
            buttons: buttons,
            width: width,
            height: height,
            overflow: 'hidden',
            color: 'var(--theme-background-negative)',
            speed: '0.3',
            opacity: '0.8',
            modal: true,
            showClose: showClose,
            showMax: true,
            onOpen: function (event) {
                window.apm.removedNode = node,
                    window.apm.treeNode = treeNode;
                // Open dialog event
                if (openCallback) {
                    if(Array.isArray(openCallback)){
                        const selector = openCallback[0];
                        const callback = openCallback[1];

                        this.intervalHandle = setInterval(() => {
                            if($(selector)){
                                clearInterval(this.intervalHandle);
                                callback();
                            }
                        }, 10);

                    } else {
                        console.error("unsuported input")
                    }
                    
                }
            },
            onClose: function (event) {
                // Close dialog event
                document.dispatchEvent(new CustomEvent('ev-dialog-close'));
            },

            onMax: function (event) {
            },
            onMin: function (event) {
            },
            onToggle: function (event) {
                // Resize dialog window event
                event.onComplete = function () {
                    const canvasEv = new CustomEvent('ev-dialog-toggle');
                    document.dispatchEvent(canvasEv);
                };
            },
            onKeydown: function (event) {
            }
        });
    }
    /**
     * Show message popup inside dialog
     * @param {string} text
     */
    popupMessage(text,commonModule=null) {
        w2popup.message({
            width: 400,
            height: 200,
            html: '<div class="element-body" style="padding: 50px; text-align: center">' + text + '</div>' +
                '<div class="element-buttons"><button class="w2ui-btn secondary" onclick="w2popup.message()">Close</button></div>',
                onClose:()=>{
                    window.apm.commonModuleManager.importCommonModule(commonModule);
                    document.dispatchEvent(new CustomEvent('ev-dialog-close'));
                    w2popup.close();
                }
            })
    }
    /**
     * Return true, if it is a common Module
     */
    isCommonModule() {
        return this.serialized.client === 'InteractionsCommon';
    }

    registerCommonModule(commonModule, replace){
        const registeredCommonModules = window.apm.serialized.appCommonModules || [];
        const moduleInfo = {
            _id: commonModule._id,
            application: commonModule.application,
            version: commonModule.version,
            author: commonModule.author
        }

        if(replace){
            const idx = registeredCommonModules.find(rcm => rcm._id === commonModule._id);
            registeredCommonModules.splice(idx, 1);
        }
        
        registeredCommonModules.push(moduleInfo);
        this.refreshApplicationTree();
    }

    importGraphQLSchemasFromCommonModule(commonModule, id, name){
        if (commonModule.graphQLSchemas) {
            window.apm.serialized.graphQLSchemas = window.apm.serialized.graphQLSchemas || [];
                // if schema does not exist in this application, add common module keys, then add to serialized.graphQLSchemas
                commonModule.graphQLSchemas.forEach(commonModuleSchema => {
                    if(window.apm.serialized.graphQLSchemas.findIndex(gQL => gQL.id === commonModuleSchema.id) === -1){
                        commonModuleSchema.moduleId = id; 
                        commonModuleSchema.moduleApp = name; 
                        commonModuleSchema.isCommonModule = true; 
                        commonModuleSchema.createdTime = this.timestamp; 
                        commonModuleSchema.version = commonModule.version;
                        window.apm.serialized.graphQLSchemas.push(commonModuleSchema);
                    }
                });
        }
        this.setSerializedChanged();
        // see DEVDES2-1457
        renderByRoute(RootRoute.settings);
    }

    importCSVDataFromCommonModule(commonModule, id, name){
        if (commonModule.csvData) {
            // if csv is not found in application, mark it and add to serialized.csvData
            window.apm.serialized.csvData = window.apm.serialized.csvData || [];
                commonModule.csvData.forEach(commonModuleCSVData => {
                    if(window.apm.serialized.csvData.findIndex(csv => csv.id === commonModuleCSVData.id) === -1){
                        commonModuleCSVData.moduleId = id; 
                        commonModuleCSVData.moduleApp = name;
                        commonModuleCSVData.isCommonModule = true; 
                        commonModuleCSVData.createdTime = this.timestamp; 
                        commonModuleCSVData.version = commonModule.version;
                        window.apm.serialized.csvData.push(commonModuleCSVData);
                    }
                });
            
        }
        this.setSerializedChanged();
        renderByRoute(RootRoute.settings);
    }

    removeGraphQLSchemasFromCommonModule(existingModuleId){
        if (window.apm.serialized.graphQLSchemas) {
            window.apm.serialized.graphQLSchemas = window.apm.serialized.graphQLSchemas.filter(schema => {
                if(schema.moduleId === existingModuleId){
                    this.updateDeletedGqlSchemaMap(schema.id);
                } else {
                    return schema;
                }
            });
            window.apm.serialized.graphQLSchemas = window.apm.serialized.graphQLSchemas.filter(schema => schema.moduleId !== existingModuleId);
            renderByRoute(RootRoute.settings);
        }
        
    }

    removeCSVDataFromCommonModule(existingModuleId){
        if(window.apm.serialized.csvData){
            window.apm.serialized.csvData = window.apm.serialized.csvData.filter(csvData => {
                if(csvData.moduleId === existingModuleId){
                    this.updateDeletedCsvDataMap(csvData.id);
                } else {
                    return csvData;
                }
            });
        }
        renderByRoute(RootRoute.settings);
    }
    /*
    *wizard Dialog
    */
    openCommonModuleWizard(commonModule, isNewElement = false, nodeId = null, ) {
        if (commonModule) {
            let appComo, appPrompts, addOrUpdateButton=window.l.popup.update;
            const rootModule = commonModule.components.modules.find(m => m.id === rootModuleId);

            if (nodeId) {
                appComo = window.apm.serialized.components.modules.find(m => m.id === nodeId);
                appPrompts = window.apm.serialized.components.objects.filter(o => o.props.nodeTypeName === NodeTypeName.prompt && o.props.moduleId === nodeId);
                }
                const prompts = commonModule.components.objects.filter(o => o.props.nodeTypeName === NodeTypeName.prompt);
                const wizardConfig = commonModule.wizardConfig ? commonModule.wizardConfig : {};
                const returnToNewElementDialog = () => {
                        ReactDOM.unmountComponentAtNode($(".app_property_container").get(0));
                        w2popup.close();
                        document.dispatchEvent(new CustomEvent("ev-open-new-element-dialog"));
                    }

                const closeWizardDialog = () => {
                    ReactDOM.unmountComponentAtNode($(".app_property_container").get(0));
                    w2popup.close();
                }
            if (isNewElement) {
                addOrUpdateButton = window.l.popup.add;
                }
                window.apm.popup(
                    "Capture Wizard",
                    '<div class="app_property_container"></div>',
                    `<button class="w2ui-btn primary add-button">${addOrUpdateButton}</button>` +
                    `<button class="w2ui-btn secondary close-button">${window.l.popup.close}</button>`,
                    [
                        ".app_property_container",

                        () => {
                            ReactDOM.render(
                                <CommonModuleWizardDialog
                                    como={commonModule}
                                    closeWizardDialog={closeWizardDialog}
                                    rootModule={rootModule}
                                    wizardConfig={wizardConfig}
                                    prompts={prompts}
                                    appPrompts={appPrompts}
                                    isNewElement={isNewElement}
                                    appComo={appComo} />,
                                $(".app_property_container").get(0)
                            );
                            $(".w2ui-popup").find(".add-button").on("click", function () {
                                if (isNewElement) {
                                    document.dispatchEvent(new CustomEvent("ev-wizard-dialog-add"));
                                }
                                else {
                                    document.dispatchEvent(new CustomEvent("ev-wizard-dialog-wizard"));
                                }

                            });
                            $(".w2ui-popup").find(".close-button").on("click", function () {
                                if (isNewElement) {
                                    returnToNewElementDialog();
                                }
                                else {
                                    closeWizardDialog();
                                }

                            });

                        }

                    ]
                );
            }
        
    }

    /**
     * @private Check if the latest version of the application has breaking changes that the opened version does not have.
     */
    _checkForBreakingChanges() {
        if (
            // Wait for serialized & latestSerialized before checking for breaks.
            !this.serialized
            || !this.latestSerialized
            // Do not display the dialog if the user already knows there are breaking changes.
            || this._breakingChangesExist
            // should not matter in nocollab as these changes do not get meged into latest version
            || DesignerSetting.s.is_collab === "false"
        ) {
            return;
        }

        this._breakingChangesExist = BreakingChangesDialog.popupIfBreakingChangesExist({
            currentVersion: this.serialized,
            savedVersion: this.latestSerialized
        });
    }

    /**
     * Determines if the current application has a save prompt object within it.
     * @returns {boolean}
     */
    appHasSavePromptObjects() {
        return this.serialized.components.objects.some((x) => x._class === ObjectClass.savePrompt);
    }

    /** @private */
    _itterateItemsWithInputs(updateInputItem, moduleId, serialized) {
        // Skip refreshing the canvas for unit tests.
        if (!window.w2ui) {
            return;
        }

        const { timestamp } = window.apm;
        serialized.globalIntents.forEach((globalIntent) => {
            if (
                globalIntent.module === moduleId
                && updateInputItem(globalIntent.moduleProperties)
            ) {
                globalIntent.lastLocalChange = timestamp;
            }
        });

        serialized.components.modules.forEach((mod) => {
            if (mod.id === moduleId && updateInputItem(mod)) {
                mod.props.lastLocalChange = timestamp;
            }

            mod.versions[1].flow.nodes.forEach((node) => {
                if (
                    node.props.parentComponentID === moduleId
                    && updateInputItem(node)
                ) {
                    node.props.lastLocalChange = timestamp;
                    this.postEventToAllViews({
                        target: "request-refresh",
                        event: {
                            id: node.id,
                            props: node.props,
                            type: "node",
                            upDatedField: "inputs"
                        }
                    });
                }
            });
        });
    }

    _updateCurrentModuleStore() {
        const { activeTabId } = useCanvasTabs.getState();
        const currentModule = this.serialized.components.modules.find((appMod) => {
            return appMod.id === activeTabId;
        }) ?? null;
        useCurrentModule.setState(currentModule);
    }
}

export default AppManager;
